// src/components/StorePlantEditor.js
// Editor de planta baixa — 4 modos: Perímetro, Elementos, Seções, Rota
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Stage, Layer, Circle, Line, Rect, Text, Transformer, Group } from 'react-konva';
import { useConfirm } from './shared/ConfirmDialog';
import useStorePlantAuthoring from './useStorePlantAuthoring';
import CadastralClarificationReview from './CadastralClarificationReview';
import StorePlantItemsEditor from './StorePlantItemsEditor';
import usePlantClarificationIndex, { CADASTRAL_PILOT } from './usePlantClarificationIndex';
import './StorePlantContext.css';
import {
  applyEditorPointPolicy,
  applySectionPointPolicy,
  distanceBetweenPoints,
} from '../utils/storePlantGeometry';
import {
  applyRouteStopTypedEdit,
  createRouteStop,
} from '../utils/storePlantRouteIdentity';

const DIST_FECHAR = 14;
const MAP_W = 3000;
const MAP_H = 2000;
const ENTRY_FACT_SCHEMA_VERSION = 'indoor-entry-fact-source/v1';
const SECTION_NAME_MAX_LENGTH = 40;
const SERVICE_DECLARATION_ID_PREFIX = 'service-declaration:v1:';

function createServiceDeclarationId() {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return `${SERVICE_DECLARATION_ID_PREFIX}${cryptoApi.randomUUID().toLowerCase()}`;
  }
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    const uuid = [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
    return `${SERVICE_DECLARATION_ID_PREFIX}${uuid}`;
  }
  throw new Error('SERVICE_DECLARATION_ID_GENERATION_UNAVAILABLE');
}

const TIPOS_ELEMENTO = [
  { tipo: 'gondola', label: '🛒 Gôndola',  w: 120, h: 40, cor: '#6b7280' },
  { tipo: 'ilha',    label: '🏝️ Ilha',     w: 80,  h: 80, cor: '#9ca3af' },
  { tipo: 'balcao',  label: '🏦 Balcão',   w: 140, h: 30, cor: '#78716c' },
  { tipo: 'caixa',   label: '🧾 Caixa',    w: 50,  h: 50, cor: '#854d0e' },
  { tipo: 'estacionamento', label: '🅿️ Estacionamento', w: 120, h: 50, cor: '#0f766e' },
];

const SERVICOS = Object.freeze([
  { key: 'POSTAL', label: 'Atendimento postal' },
  { key: 'DISPATCH', label: 'Expedição' },
  { key: 'PICKUP', label: 'Retirada de pedidos' },
]);

const CORES_SECAO = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

const SPATIAL_WITNESS_COMPLEXITY_CODE = 'PLANT_SPATIAL_WITNESS_COMPLEXITY_LIMIT_EXCEEDED';

const SPATIAL_WITNESS_METRIC_LABELS = Object.freeze({
  sectionsCount: 'quantidade de seções',
  elementsCount: 'quantidade de elementos',
  routeStopsCount: 'quantidade de paradas da rota',
  perimeterVerticesCount: 'vértices do perímetro',
  sectionPolygonVerticesCount: 'vértices de uma seção',
  totalPolygonVerticesCount: 'vértices totais da planta',
  sectionOverlapPairsCount: 'pares de sobreposição entre seções',
  geometryWorkUnits: 'unidades de processamento geométrico',
});

const SPATIAL_WITNESS_CATEGORY_LABELS = Object.freeze({
  perimeterRaster: 'leitura do perímetro',
  sectionRaster: 'leitura das seções',
  sectionOverlap: 'análise de sobreposições',
  elementOwnership: 'associação espacial dos elementos',
  routeStopRaster: 'leitura das paradas da rota',
  routeStopOwnership: 'associação espacial das paradas',
});

function safeComplexityCount(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function explainPlantSaveError(payload) {
  const data = payload && typeof payload === 'object' ? payload : {};
  const fallback = typeof data.erro === 'string' && data.erro.trim()
    ? data.erro
    : 'Erro ao salvar planta.';
  if (data.code === 'PLANT_MERCHANDISE_ORTHOGRAPHY_REVIEW_REQUIRED') {
    const count = Array.isArray(data.orthographyCorrections)
      ? data.orthographyCorrections.length
      : 0;
    return count > 0
      ? `${count} correção${count > 1 ? 'ões' : ''} ortográfica${count > 1 ? 's' : ''} precisa${count > 1 ? 'm' : ''} ser revisada${count > 1 ? 's' : ''} antes de salvar.`
      : fallback;
  }
  if (data.code === 'MINIMUM_OPERATIONAL_PLANT_REQUIRED') {
    const firstIssue = Array.isArray(data.minimumOperationalPlant?.issues)
      ? data.minimumOperationalPlant.issues[0]
      : null;
    return firstIssue?.message
      ? `Planta mínima incompleta: ${firstIssue.message}`
      : fallback;
  }
  if (data.code === 'MINIMUM_OPERATIONAL_SECTION_DIVERSITY_REQUIRED') {
    const diversity = data.operationalSectionDiversity || {};
    const required = Number.isInteger(diversity.minimumRequiredSections)
      ? diversity.minimumRequiredSections
      : 3;
    const actual = Number.isInteger(diversity.eligibleSectionCount)
      ? diversity.eligibleSectionCount
      : 0;
    return `Cobertura operacional incompleta: ${actual}/${required} seções possuem mercadoria e parada de rota identificada.`;
  }
  if (data.code !== SPATIAL_WITNESS_COMPLEXITY_CODE) return fallback;

  const details = data.details && typeof data.details === 'object' && !Array.isArray(data.details)
    ? data.details
    : {};
  const metricLabel = Object.prototype.hasOwnProperty.call(SPATIAL_WITNESS_METRIC_LABELS, details.metric)
    ? SPATIAL_WITNESS_METRIC_LABELS[details.metric]
    : null;
  const categoryLabel = Object.prototype.hasOwnProperty.call(SPATIAL_WITNESS_CATEGORY_LABELS, details.category)
    ? SPATIAL_WITNESS_CATEGORY_LABELS[details.category]
    : null;
  const actual = safeComplexityCount(details.actual);
  const limit = safeComplexityCount(details.limit);
  const description = [];

  if (metricLabel && actual !== null && limit !== null) {
    const formatter = new Intl.NumberFormat('pt-BR');
    description.push(`Métrica: ${metricLabel}; observado: ${formatter.format(actual)}; limite: ${formatter.format(limit)}.`);
  }
  if (categoryLabel) description.push(`Etapa: ${categoryLabel}.`);

  return [
    'A planta excedeu o limite seguro de processamento espacial.',
    ...description,
    'Reduza a complexidade geométrica da planta e tente novamente.',
  ].join(' ');
}

function pontoNaSecao(ponto, secao) {
  const pts = secao.pontos;
  if (!pts || pts.length < 3) return false;
  let inside = false;
  let j = pts.length - 1;
  for (let i = 0; i < pts.length; i++) {
    const xi = pts[i].x, yi = pts[i].y;
    const xj = pts[j].x, yj = pts[j].y;
    if ((yi > ponto.y) !== (yj > ponto.y) && ponto.x < ((xj - xi) * (ponto.y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
    j = i;
  }
  return inside;
}

function encontrarSecao(ponto, secoes) {
  return secoes.find(s => s.pontos.length >= 3 && pontoNaSecao(ponto, s)) || null;
}

function normalizeForCompare(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function sameLabel(a, b) {
  const left = normalizeForCompare(a);
  const right = normalizeForCompare(b);
  if (!left || !right) return false;
  return left === right;
}

function declaredSectionNameError(value) {
  const name = String(value || '').trim();
  if (!name) return 'Informe o nome da seção.';
  if (name.length > SECTION_NAME_MAX_LENGTH || /[\r\n]/u.test(name)) {
    return `Use um nome de até ${SECTION_NAME_MAX_LENGTH} caracteres, em uma única linha.`;
  }
  if (/^(?:secao|setor)\s+\d+$/u.test(normalizeForCompare(name))) {
    return 'Troque o nome automático por uma designação própria da seção.';
  }
  return '';
}

function normalizeSemanticHints(value) {
  const source = Array.isArray(value)
    ? value
    : (typeof value === 'string' ? value.split(/[,;\n]+/) : []);
  const seen = new Set();

  return source
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .filter(item => {
      const key = normalizeForCompare(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function semanticHintsWithSuggestion(currentHints, suggestion) {
  const hints = normalizeSemanticHints(currentHints);
  const term = String(suggestion || '').trim();
  const key = normalizeForCompare(term);
  if (!key) return hints;
  if (hints.some(item => normalizeForCompare(item) === key)) return hints;
  return [...hints, term];
}

function tipoElementoLabel(tipo) {
  return TIPOS_ELEMENTO.find(t => t.tipo === tipo)?.label.replace(/^\S+\s/, '') || tipo || 'Elemento';
}

function rotuloElementoPorSecao(tipo, secaoNome) {
  const tipoLabel = tipoElementoLabel(tipo);
  const secao = String(secaoNome || '').trim();
  return secao ? `${tipoLabel} de ${secao}` : `${tipoLabel} sem seção`;
}

function nomePareceRotuloAutomatico(nome, tipo, secaoNome) {
  const raw = String(nome || '').trim();
  const tipoLabel = tipoElementoLabel(tipo);
  if (!raw || raw === tipoLabel) return true;
  if (/^este elemento pertence/i.test(raw)) return true;
  if (raw === rotuloElementoPorSecao(tipo, secaoNome)) return true;
  return TIPOS_ELEMENTO.some(item => {
    const label = tipoElementoLabel(item.tipo);
    return raw === `${label} sem seção` || raw.startsWith(`${label} de `);
  });
}

function centroElemento(elemento) {
  return {
    x: Number(elemento?.x || 0) + Number(elemento?.width || 0) / 2,
    y: Number(elemento?.y || 0) + Number(elemento?.height || 0) / 2,
  };
}

function atualizarPertencimentoElemento(elemento, secoes, patch = {}) {
  const base = { ...elemento, ...patch };
  if (base.tipo === 'entrada' || base.tipo === 'servico') return base;
  const secao = encontrarSecao(centroElemento(base), secoes);
  const secaoNome = secao?.nome || '';
  const nextSecaoId = sectionId(secao);
  const shouldAutoLabel = nomePareceRotuloAutomatico(elemento?.nome, base.tipo, elemento?.secao);
  return {
    ...base,
    secao: secaoNome,
    secaoId: nextSecaoId,
    nome: shouldAutoLabel ? rotuloElementoPorSecao(base.tipo, secaoNome) : base.nome,
  };
}

function looksLikeItemList(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  return /[,;\n]/.test(text) || (/\b(e|ou)\b/i.test(text) && text.split(/\s+/).length >= 4);
}

function draftItemsText(values) {
  const flat = (values || []).flatMap(value => Array.isArray(value) ? value : [value]);
  return normalizeSemanticHints(flat.filter(Boolean).join('\n')).join('\n');
}

function draftEdicaoElemento(elemento) {
  const tipoLabel = tipoElementoLabel(elemento?.tipo);
  const rawName = String(elemento?.nome || '').trim();
  const hints = normalizeSemanticHints(elemento?.semanticHints);

  if (!rawName || rawName === tipoLabel) {
    return { nome: '', itens: hints.join('\n') };
  }

  let parsed = null;
  try { parsed = JSON.parse(rawName); } catch {}
  if (parsed && typeof parsed === 'object') {
    return {
      nome: String(parsed.nomeDisplay || parsed.nome || tipoLabel || '').trim(),
      itens: draftItemsText([
        ...hints,
        parsed.descricao,
        parsed.itens,
        parsed.items,
        parsed.produtos,
        parsed.products,
      ]),
    };
  }

  const linhas = rawName.split('\n').map(linha => linha.trim()).filter(Boolean);
  if (linhas.length > 1) {
    return {
      nome: linhas[0] === tipoLabel ? '' : linhas[0],
      itens: draftItemsText([
        ...hints,
        linhas.slice(1).join('\n'),
      ]),
    };
  }

  if (looksLikeItemList(rawName)) {
    return {
      nome: '',
      itens: draftItemsText([...hints, rawName]),
    };
  }

  return { nome: rawName, itens: hints.join('\n') };
}

function plantSignature({ perimetro, elementos, secoes, rota }) {
  return JSON.stringify({ perimetro, elementos, secoes, rota });
}

function clonePlantState(plant) {
  return JSON.parse(JSON.stringify({
    perimetro: Array.isArray(plant?.perimetro) ? plant.perimetro : [],
    elementos: Array.isArray(plant?.elementos) ? plant.elementos : [],
    secoes: Array.isArray(plant?.secoes) ? plant.secoes : [],
    rota: Array.isArray(plant?.rota) ? plant.rota : [],
  }));
}

function stableStringify(value) {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(item => stableStringify(item) ?? 'null').join(',')}]`;
  return `{${Object.keys(value).sort()
    .map(key => {
      const serialized = stableStringify(value[key]);
      if (serialized === undefined) return null;
      return `${JSON.stringify(key)}:${serialized}`;
    })
    .filter(Boolean)
    .join(',')}}`;
}

function canonicalSectionName(secao) {
  return String(secao?.nome || secao?.sectionName || secao?.name || '').trim();
}

function sectionId(secao) {
  return String(secao?.id || secao?.sectionId || secao?.secaoId || '').trim();
}

function sectionKey(sectionName) {
  return normalizeForCompare(sectionName);
}

function sectionIdentityKey(secao) {
  const id = sectionId(secao);
  if (id) return `section-id:${id}`;
  return `section-name:${sectionKey(canonicalSectionName(secao))}`;
}

function sectionKeysFor(secoes = []) {
  return (secoes || [])
    .map(sectionIdentityKey)
    .filter(Boolean)
    .sort();
}

function hasDuplicateSectionLabels(secoes = []) {
  const seen = new Set();
  for (const secao of secoes || []) {
    const key = sectionKey(canonicalSectionName(secao));
    if (!key) continue;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

function sameKeyList(a, b) {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item === b[index]);
}

function inferElementSectionName(elemento, secoes) {
  const explicitId = String(elemento?.secaoId || elemento?.sectionId || '').trim();
  if (explicitId) {
    const secao = secoes.find(s => sectionId(s) === explicitId);
    if (secao) return canonicalSectionName(secao);
  }
  return elemento?.secao || encontrarSecao(centroElemento(elemento), secoes)?.nome || '';
}

function inferRouteSectionName(parada, secoes) {
  const explicitId = String(parada?.secaoId || parada?.sectionId || '').trim();
  if (explicitId) {
    const secao = secoes.find(s => sectionId(s) === explicitId);
    if (secao) return canonicalSectionName(secao);
  }
  if (parada?.secao || parada?.sectionName || parada?.setor) {
    return parada.secao || parada.sectionName || parada.setor;
  }
  return parada?.posicao ? encontrarSecao(parada.posicao, secoes)?.nome || '' : '';
}

function inferElementSectionKey(elemento, secoes) {
  const explicitId = String(elemento?.secaoId || elemento?.sectionId || '').trim();
  if (explicitId) {
    const secao = secoes.find(s => sectionId(s) === explicitId);
    if (secao) return sectionIdentityKey(secao);
  }
  const secao = encontrarSecao(centroElemento(elemento), secoes)
    || secoes.find(s => sameLabel(canonicalSectionName(s), elemento?.secao));
  return secao ? sectionIdentityKey(secao) : sectionKey(inferElementSectionName(elemento, secoes));
}

function inferRouteSectionKey(parada, secoes) {
  const explicitId = String(parada?.secaoId || parada?.sectionId || '').trim();
  if (explicitId) {
    const secao = secoes.find(s => sectionId(s) === explicitId);
    if (secao) return sectionIdentityKey(secao);
  }
  const secao = parada?.posicao
    ? encontrarSecao(parada.posicao, secoes)
    : secoes.find(s => sameLabel(canonicalSectionName(s), inferRouteSectionName(parada, secoes)));
  return secao ? sectionIdentityKey(secao) : sectionKey(inferRouteSectionName(parada, secoes));
}

function sectionEvidencePreviewMap(plant) {
  const map = {};
  const secoes = Array.isArray(plant?.secoes) ? plant.secoes : [];
  const keys = sectionKeysFor(secoes);
  for (const key of keys) map[key] = { elements: [], route: [] };

  const global = { elements: [], route: [] };
  for (const elemento of Array.isArray(plant?.elementos) ? plant.elementos : []) {
    const key = inferElementSectionKey(elemento, secoes);
    const bucket = key && map[key] ? map[key].elements : global.elements;
    bucket.push({
      id: elemento.id || null,
      tipo: elemento.tipo || '',
      nome: elemento.nome || '',
      semanticHints: Array.isArray(elemento.semanticHints) ? elemento.semanticHints : [],
      x: elemento.x ?? null,
      y: elemento.y ?? null,
      width: elemento.width ?? null,
      height: elemento.height ?? null,
      rotation: elemento.rotation ?? null,
    });
  }

  for (const [index, parada] of (Array.isArray(plant?.rota) ? plant.rota : []).entries()) {
    const key = inferRouteSectionKey(parada, secoes);
    const bucket = key && map[key] ? map[key].route : global.route;
    bucket.push({
      index,
      direita: parada.direita || '',
      esquerda: parada.esquerda || '',
      frente: parada.frente || '',
      posicao: parada.posicao || null,
    });
  }

  for (const value of Object.values(map)) {
    value.elements.sort((a, b) => stableStringify(a).localeCompare(stableStringify(b)));
    value.route.sort((a, b) => a.index - b.index);
  }
  global.elements.sort((a, b) => stableStringify(a).localeCompare(stableStringify(b)));
  global.route.sort((a, b) => a.index - b.index);

  return { map, global };
}

function buildReprocessPreview(savedPlant, draftPlant) {
  if (!savedPlant) {
    return { scope: 'UNKNOWN', changedSections: [], reason: 'NO_SAVED_PLANT' };
  }
  if (plantSignature(savedPlant) === plantSignature(draftPlant)) {
    return { scope: 'NONE', changedSections: [], reason: 'UNCHANGED' };
  }

  const savedSectionKeys = sectionKeysFor(savedPlant.secoes);
  const draftSectionKeys = sectionKeysFor(draftPlant.secoes);
  if (!sameKeyList(savedSectionKeys, draftSectionKeys)) {
    return { scope: 'FULL', changedSections: [], reason: 'SECTION_SET_CHANGED' };
  }
  if (stableStringify(savedPlant.perimetro || []) !== stableStringify(draftPlant.perimetro || [])) {
    return { scope: 'FULL', changedSections: [], reason: 'PERIMETER_CHANGED' };
  }
  if (stableStringify(savedPlant.secoes || []) !== stableStringify(draftPlant.secoes || [])) {
    return { scope: 'FULL', changedSections: [], reason: 'SECTION_GEOMETRY_CHANGED' };
  }
  if (hasDuplicateSectionLabels(savedPlant.secoes) || hasDuplicateSectionLabels(draftPlant.secoes)) {
    return { scope: 'FULL', changedSections: [], reason: 'DUPLICATE_SECTION_LABELS_REQUIRE_FULL' };
  }

  const savedEvidence = sectionEvidencePreviewMap(savedPlant);
  const draftEvidence = sectionEvidencePreviewMap(draftPlant);
  if (stableStringify(savedEvidence.global) !== stableStringify(draftEvidence.global)) {
    return { scope: 'FULL', changedSections: [], reason: 'GLOBAL_EVIDENCE_CHANGED' };
  }

  const changedKeys = draftSectionKeys.filter(key =>
    stableStringify(savedEvidence.map[key] || {}) !== stableStringify(draftEvidence.map[key] || {})
  );
  if (changedKeys.length === 0) {
    return { scope: 'FULL', changedSections: [], reason: 'STRUCTURE_CHANGED_WITHOUT_SECTION_EVIDENCE' };
  }

  const sectionNamesByKey = new Map((draftPlant.secoes || [])
    .map(secao => [sectionIdentityKey(secao), canonicalSectionName(secao)])
    .filter(([, name]) => name));

  return {
    scope: 'SECTION',
    changedSections: changedKeys.map(key => sectionNamesByKey.get(key) || key),
    reason: 'SECTION_EVIDENCE_CHANGED',
  };
}

export default function StorePlantEditor({ clienteId, focusSectionRef = null, focusSectionName = null, focusSource = null }) {
  const { confirm, ConfirmDialogPortal } = useConfirm();
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const perimetroLayerRef = useRef(null);
  const secoesLayerRef = useRef(null);
  const elementosLayerRef = useRef(null);
  const rotaLayerRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });

  const [modo, setModo] = useState('perimetro');

  // ── Modo 1: Perímetro ─────────────────────────────────────────────────────
  const [perimetro, setPerimetro] = useState([]);
  const [fechado, setFechado] = useState(false);
  const [perimetroSel, setPerimetroSel] = useState(false);

  // ── Modo 2: Elementos ─────────────────────────────────────────────────────
  const [elementos, setElementos] = useState([]);
  const [tipoAdicionar, setTipoAdicionar] = useState(null);
  const [elementoSel, setElementoSel] = useState(null);
  const [editandoNome, setEditandoNome] = useState(null);
  const [itemAspectDrafts, setItemAspectDrafts] = useState([]);
  const [clarificationRefreshSeq, setClarificationRefreshSeq] = useState(0);
  const [plantView, setPlantView] = useState({ x: 0, y: 0, scale: 1 });
  const [editandoEntrada, setEditandoEntrada] = useState(null);
  const [focusedAdviceSectionName, setFocusedAdviceSectionName] = useState('');
  const [entradaTemp, setEntradaTemp] = useState({
    nomeDisplay: '',
    carrinhos: '',
    visaoGeral: '',
    sensacao: '',
    displayNameSealed: false,
  });
  const [editandoServico, setEditandoServico] = useState(null);
  const [serviceEdited, setServiceEdited] = useState(false);
  const serviceEditorRef = useRef(null);
  useEffect(() => { if (editandoServico) serviceEditorRef.current?.scrollIntoView?.({ block: 'center' }); }, [editandoServico]);
  const [servicoTemp, setServicoTemp] = useState({
    nomeDisplay: 'Serviço',
    serviceKey: '',
    nomeServico: '',
    descricao: '',
    serviceDeclarationId: null,
  });
  const transformerRef = useRef(null);
  const elementRefs = useRef({});

  // ── Modo 3: Seções ────────────────────────────────────────────────────────
  const [secoes, setSecoes] = useState([]);
  const [secaoEmDesenho, setSecaoEmDesenho] = useState(null);
  const [secaoSel, setSecaoSel] = useState(null);
  const [renomeandoSecao, setRenomeandoSecao] = useState(null);
  const [secaoNovaPendenteId, setSecaoNovaPendenteId] = useState(null);
  const [nomeSecaoTemp, setNomeSecaoTemp] = useState('');
  const [diasTemp, setDiasTemp] = useState([0,1,2,3,4,5,6]);
  const [pesoSecaoTemp, setPesoSecaoTemp] = useState(5);
  const [sensivelSecaoTemp, setSensivelSecaoTemp] = useState(false);

  // ── Modo 4: Rota ──────────────────────────────────────────────────────────
  const [rota, setRota] = useState([]);
  const [cursorPos, setCursorPos] = useState(null);
  const [rotaSel, setRotaSel] = useState(null);
  const [editandoParadaModal, setEditandoParadaModal] = useState(null);
  const [paradaModalTemp, setParadaModalTemp] = useState({
    direita: '',
    esquerda: '',
    frente: '',
    relation: '',
    targetKey: '',
  });
  const spatialTargetOptions = useMemo(() => {
    const options = [];
    secoes.forEach(secao => {
      const id = sectionId(secao);
      const name = String(secao?.nome || secao?.sectionName || '').trim();
      if (!id || !name) return;
      const target = {
        type: 'SECTION',
        sectionId: id,
        sectionKey: `section-id:${id}`,
        sectionName: name,
      };
      options.push({ key: JSON.stringify(target), label: `Seção: ${name}` });
    });
    elementos.forEach(elemento => {
      const elementId = String(elemento?.elementId || elemento?.id || '').trim();
      if (!elementId) return;
      normalizeSemanticHints(elemento?.semanticHints).forEach(value => {
        const target = { type: 'ITEM_CATEGORY', elementId, value };
        options.push({ key: JSON.stringify(target), label: `Item: ${value}` });
      });
      if (
        elemento?.tipo === 'entrada'
        && elemento?.entryFactSchemaVersion === ENTRY_FACT_SCHEMA_VERSION
        && elemento?.exists === true
      ) {
        const dados = dadosEntradaElemento(elemento);
        const target = { type: 'ENTRANCE', elementId };
        options.push({
          key: JSON.stringify(target),
          label: `Entrada: ${dados?.nomeDisplay || 'entrada da loja'}`,
        });
      }
    });
    return options;
  }, [elementos, secoes]);

  // ── Geral ─────────────────────────────────────────────────────────────────
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState('');
  const [semanticAdvice, setSemanticAdvice] = useState(null);
  const [minimumOperationalPlant, setMinimumOperationalPlant] = useState(null);
  const [operationalSectionDiversity, setOperationalSectionDiversity] = useState(null);
  const [semanticAdviceLoading, setSemanticAdviceLoading] = useState(false);
  const [semanticAdviceError, setSemanticAdviceError] = useState('');
  const [pendingOrthographyCorrections, setPendingOrthographyCorrections] = useState([]);
  const [semanticAdviceRefreshSeq, setSemanticAdviceRefreshSeq] = useState(0);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorTab, setInspectorTab] = useState('semantica');
  const [plantSaveState, setPlantSaveState] = useState('idle');
  const [processingDetail, setProcessingDetail] = useState('');
  const plantLoadedRef = useRef(false);
  const lastSavedPlantSignatureRef = useRef('');
  const lastSavedPlantRef = useRef(null);
  const dragStartPointRef = useRef(null);
  const authoring = useStorePlantAuthoring(clienteId, d => {
    const nextPerimetro = Array.isArray(d.perimetro) ? d.perimetro : [];
    const nextElementos = Array.isArray(d.elementos) ? d.elementos : [];
    const nextSecoes = Array.isArray(d.secoes)
      ? d.secoes.map(section => {
          const liveSection = { ...section };
          delete liveSection.categoria;
          return {
            ...liveSection,
            sensivel: section?.sensivel === true,
          };
        })
      : [];
    const nextRota = Array.isArray(d.rota) ? d.rota : [];

    setPerimetro(nextPerimetro);
    setFechado(nextPerimetro.length >= 3);
    setElementos(nextElementos);
    setSecoes(nextSecoes);
    setRota(nextRota);
    setSemanticAdvice(d.semanticDataQuality || null);
    setPerimetroSel(false);
    setElementoSel(null);
    setSecaoSel(null);
    setRotaSel(null);
    setTipoAdicionar(null);
    setSecaoEmDesenho(null);
    setRenomeandoSecao(null);
    setSecaoNovaPendenteId(null);
    setEditandoNome(null);
    setItemAspectDrafts([]);
    setEditandoEntrada(null);
    setEditandoServico(null);
    setEditandoParadaModal(null);
    setPendingOrthographyCorrections([]);
    setMinimumOperationalPlant(d.minimumOperationalPlant || null);
    setOperationalSectionDiversity(d.operationalSectionDiversity || null);
    const savedPlant = {
      perimetro: nextPerimetro,
      elementos: nextElementos,
      secoes: nextSecoes,
      rota: nextRota,
    };
    lastSavedPlantSignatureRef.current = plantSignature(savedPlant);
    lastSavedPlantRef.current = clonePlantState(savedPlant);
    plantLoadedRef.current = true;
    setPlantSaveState('idle');
    setMsg('');
  });
  const editorLockedByProcessing = !authoring.editable || salvando;
  const saveDisabled = editorLockedByProcessing;
  const clarificationReady = !editorLockedByProcessing && itemAspectDrafts.length === 0
    && !(editandoServico && serviceEdited)
    && lastSavedPlantSignatureRef.current === plantSignature({ perimetro, elementos, secoes, rota });
  const clarificationIndex = usePlantClarificationIndex({ tenantId: clienteId, revision: authoring.revision,
    ready: clarificationReady, refreshToken: clarificationRefreshSeq });
  const nomeSecaoErro = declaredSectionNameError(nomeSecaoTemp);
  const cadastroSecaoInvalido = Boolean(nomeSecaoErro)
    || diasTemp.length === 0
    || !Number.isInteger(pesoSecaoTemp)
    || pesoSecaoTemp < 1
    || pesoSecaoTemp > 10;

  function rememberDragStartPoint(event) {
    dragStartPointRef.current = {
      x: event.target.x(),
      y: event.target.y(),
    };
  }

  function consumeDragStartPoint() {
    const point = dragStartPointRef.current;
    dragStartPointRef.current = null;
    return point;
  }



  useEffect(() => {
    if (secoes.length === 0) return;
    const targetKey = String(focusSectionRef?.sectionKey || '').trim();
    const targetId = String(focusSectionRef?.sectionId || '').trim();
    const targetName = String(focusSectionRef?.sectionName || focusSectionName || '').trim();
    let matches = [];
    if (targetKey) {
      matches = secoes.filter(s => sectionIdentityKey(s) === targetKey);
    } else if (targetId) {
      matches = secoes.filter(s => sectionId(s) === targetId);
    } else if (targetName) {
      matches = secoes.filter(s => sameLabel(s.nome, targetName));
    }
    const secao = matches.length === 1 ? matches[0] : null;
    if (!secao) return;
    setModo('elementos');
    setSecaoSel(secao.id);
    setInspectorOpen(true);
    setInspectorTab('semantica');
    setFocusedAdviceSectionName(secao.nome);
    if (focusSource === 'semantic-review') {
      setProcessingDetail(`Revise a questão técnica em ${secao.nome}; salve somente se decidir alterar a planta`);
    }
  }, [focusSectionRef, focusSectionName, focusSource, secoes]);

  useEffect(() => {
    if (!plantLoadedRef.current) return;
    if (salvando) return;
    const currentSignature = plantSignature({ perimetro, elementos, secoes, rota });
    if (currentSignature === lastSavedPlantSignatureRef.current && itemAspectDrafts.length === 0) {
      if (plantSaveState === 'dirty') setPlantSaveState('idle');
      return;
    }

    if (plantSaveState === 'dirty') return;

    setMinimumOperationalPlant(null);
    setOperationalSectionDiversity(null);
    setPlantSaveState('dirty');
    setProcessingDetail('');
  }, [perimetro, elementos, secoes, rota, salvando, plantSaveState, itemAspectDrafts]);

  // ── Advisor semântico em tempo real ───────────────────────────────────────
  useEffect(() => {
    if (!clienteId || !authoring.editable) return;
    if (secaoNovaPendenteId) return;
    const hasDraft = perimetro.length > 0 || elementos.length > 0 || secoes.length > 0 || rota.length > 0;
    if (!hasDraft) {
      setSemanticAdvice(null);
      setMinimumOperationalPlant(null);
      setOperationalSectionDiversity(null);
      setSemanticAdviceError('');
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSemanticAdviceLoading(true);
      setSemanticAdviceError('');
      try {
        const response = await fetch(`/api/store-map/${clienteId}/planta/semantic-advice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ perimetro, elementos, secoes, rota }),
          signal: controller.signal,
        });
        const data = await response.json();
        if (controller.signal.aborted) return;
        if (!response.ok || !data.ok) {
          throw new Error(data.erro || 'Falha ao analisar planta');
        }
        setSemanticAdvice(data.semanticDataQuality || null);
        setMinimumOperationalPlant(data.minimumOperationalPlant || null);
        setOperationalSectionDiversity(data.operationalSectionDiversity || null);
      } catch (error) {
        if (!controller.signal.aborted && error.name !== 'AbortError') {
          setSemanticAdviceError('Sugestões indisponíveis agora.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setSemanticAdviceLoading(false);
        }
      }
    }, 650);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [clienteId, authoring.editable, perimetro, elementos, secoes, rota, semanticAdviceRefreshSeq, secaoNovaPendenteId]);

  useEffect(() => {
    if (!inspectorOpen) return undefined;
    const onKeyDown = event => {
      if (event.key === 'Escape') setInspectorOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [inspectorOpen]);

  useEffect(() => {
    if (modo !== 'secoes') return;
    setInspectorTab('secoes');
    setInspectorOpen(true);
  }, [modo]);

  // ── Stage responsivo ──────────────────────────────────────────────────────
  useEffect(() => {
    let frameId = null;

    function updateSize() {
      const container = containerRef.current;
      if (!container) return;

      const nextWidth = Math.max(1, Math.floor(container.clientWidth || container.offsetWidth || 0));
      const nextHeight = Math.max(1, Math.floor(container.clientHeight || container.offsetHeight || 0));

      setStageSize(prev => {
        if (prev.width === nextWidth && prev.height === nextHeight) return prev;
        return { width: nextWidth, height: nextHeight };
      });
    }

    function scheduleUpdateSize() {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateSize();
      });
    }

    updateSize();
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(scheduleUpdateSize);

    if (containerRef.current && observer) {
      observer.observe(containerRef.current);
    }

    window.addEventListener('resize', scheduleUpdateSize);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      if (observer) observer.disconnect();
      window.removeEventListener('resize', scheduleUpdateSize);
    };
  }, []);

  // Keep the selected element visible when the contextual editor narrows the canvas.
  useEffect(() => {
    const stage = stageRef.current;
    const element = elementos.find(el => el.id === (editandoServico || editandoNome));
    if (!element || typeof stage?.scaleX !== 'function') return;
    const scale = stage.scaleX();
    const x = (element.x + element.width / 2) * scale + stage.x();
    const y = (element.y + element.height / 2) * scale + stage.y();
    const shiftX = x < 40 || x > stageSize.width - 80 ? stageSize.width / 2 - x : 0;
    const shiftY = y < 40 || y > stageSize.height - 80 ? stageSize.height / 2 - y : 0;
    if (shiftX || shiftY) {
      stage.position({ x: stage.x() + shiftX, y: stage.y() + shiftY });
      stage.batchDraw();
      setPlantView({ x: stage.x(), y: stage.y(), scale });
    }
  }, [editandoServico, editandoNome, stageSize, elementos]);

  // ── Transformer: atualiza ao mudar seleção ────────────────────────────────
  useEffect(() => {
    if (!transformerRef.current) return;
    const node = elementoSel ? elementRefs.current[elementoSel] : null;
    transformerRef.current.nodes(node ? [node] : []);
    transformerRef.current.getLayer()?.batchDraw();
  }, [elementoSel, elementos]);

  // Camada ativa sempre fica acima das demais e apenas ela captura eventos.
  useEffect(() => {
    const activeLayer = {
      perimetro: perimetroLayerRef.current,
      elementos: elementosLayerRef.current,
      secoes: secoesLayerRef.current,
      rota: rotaLayerRef.current,
    }[modo];

    if (activeLayer) {
      activeLayer.moveToTop();
      activeLayer.getStage()?.batchDraw();
    }
  }, [modo]);

  // ── Salvar ────────────────────────────────────────────────────────────────
  function ensureEditorWritable() {
    return !editorLockedByProcessing;
  }

  async function salvar(reprocessingIntent = 'AUTO') {
    if (!clienteId || !ensureEditorWritable()) return;
    const requestedReprocessingIntent = ['FULL', 'SECTION'].includes(reprocessingIntent)
      ? reprocessingIntent
      : 'AUTO';
    setSalvando(true);
    setMsg('');
    setPlantSaveState('saving');
    setProcessingDetail('');
    try {
      const { response: r, data: d } = await authoring.commit({
        perimetro, elementos, secoes, rota, reprocessingIntent: requestedReprocessingIntent,
        ...(itemAspectDrafts.length ? { itemAspects: itemAspectDrafts.filter(item => elementos.some(el => el.id === item.elementId && el.semanticHints?.includes(item.name))) } : {}),
      });
      if (d.semanticDataQuality) setSemanticAdvice(d.semanticDataQuality);
      if (d.minimumOperationalPlant) setMinimumOperationalPlant(d.minimumOperationalPlant);
      if (d.operationalSectionDiversity) {
        setOperationalSectionDiversity(d.operationalSectionDiversity);
      }
      if (!r.ok || !d.ok) {
        if (
          d.code === 'PLANT_MERCHANDISE_ORTHOGRAPHY_REVIEW_REQUIRED'
          && Array.isArray(d.orthographyCorrections)
        ) {
          setPendingOrthographyCorrections(d.orthographyCorrections);
          setInspectorOpen(true);
          setInspectorTab('semantica');
        }
        if (
          d.code === 'MINIMUM_OPERATIONAL_PLANT_REQUIRED'
          || d.code === 'MINIMUM_OPERATIONAL_SECTION_DIVERSITY_REQUIRED'
        ) {
          setInspectorOpen(true);
          setInspectorTab('semantica');
        }
        setPlantSaveState([409, 422].includes(r.status) ? 'dirty' : 'error');
        setMsg(explainPlantSaveError(d));
        return;
      }

      const serverPlant = d.planta && typeof d.planta === 'object' ? d.planta : null;
      const savedPlant = {
        perimetro: Array.isArray(serverPlant?.perimetro) ? serverPlant.perimetro : perimetro,
        elementos: Array.isArray(serverPlant?.elementos) ? serverPlant.elementos : elementos,
        secoes: Array.isArray(serverPlant?.secoes) ? serverPlant.secoes : secoes,
        rota: Array.isArray(serverPlant?.rota) ? serverPlant.rota : rota,
      };
      if (serverPlant) {
        setPerimetro(savedPlant.perimetro);
        setElementos(savedPlant.elementos);
        setSecoes(savedPlant.secoes);
        setRota(savedPlant.rota);
        setFechado(savedPlant.perimetro.length >= 3);
      }
      lastSavedPlantSignatureRef.current = plantSignature(savedPlant);
      setItemAspectDrafts([]);
      lastSavedPlantRef.current = clonePlantState(savedPlant);
      setPendingOrthographyCorrections([]);
      if (d.deterministicRevalidationStatus === 'PERSISTENCE_ERROR') {
        setPlantSaveState('saved_warning');
        setProcessingDetail('Contexto determinístico pendente');
      } else if (d.deterministicRevalidationScope === 'SECTION') {
        setPlantSaveState('saved');
        setProcessingDetail(
          Array.isArray(d.changedSections) && d.changedSections.length > 0
            ? `Seção${d.changedSections.length > 1 ? 'ões' : ''} revalidada${d.changedSections.length > 1 ? 's' : ''}: ${d.changedSections.join(', ')}`
            : 'Seção revalidada'
        );
      } else if (d.deterministicRevalidationScope === 'FULL' || d.plantHashChanged) {
        setPlantSaveState('saved');
        setProcessingDetail('Validação determinística atualizada');
      } else {
        setPlantSaveState('unchanged');
        setProcessingDetail('Sem alteração estrutural');
      }
      setMsg(d.message || 'Planta salva.');
    } catch (error) {
      if (error.code === 'AUTHORING_SESSION_CHANGED') return;
      setPlantSaveState('error');
      setMsg(error.message || 'Erro de conexão ao salvar planta.');
    }
    finally { setSalvando(false); }
  }

  // ── Stage click (fundo) ───────────────────────────────────────────────────
  function handleStageClick(e) {
    if (!ensureEditorWritable()) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = getPointerLogical(stage);
    if (!pos) return;

    if (modo === 'perimetro') {
      setElementoSel(null);
      setSecaoSel(null);
      setRotaSel(null);
      if (fechado) return;
      if (perimetro.length >= 3 && distanceBetweenPoints(pos, perimetro[0]) < DIST_FECHAR) {
        setFechado(true);
        setPerimetroSel(true);
        return;
      }
      setPerimetro(prev => [...prev, { x: pos.x, y: pos.y }]);
      setPerimetroSel(true);
      return;
    }

    if (modo === 'elementos') {
      setPerimetroSel(false);
      setSecaoSel(null);
      setRotaSel(null);
      if (tipoAdicionar) {
        if (tipoAdicionar === 'entrada') {
          const id = `el-${Date.now()}`;
          setElementos(prev => {
            const nomeDisplay = nomePadraoNovaEntrada(prev);
            return [...prev, {
              id, elementId: id, tipo: 'entrada',
              nome: JSON.stringify({ tipo: 'entrada', nomeDisplay, carrinhos: '', visaoGeral: '', sensacao: '' }),
              entryFactSchemaVersion: ENTRY_FACT_SCHEMA_VERSION,
              exists: true,
              displayNameSealed: false,
              x: pos.x - 40, y: pos.y - 40,
              width: 80, height: 80, rotation: 0,
            }];
          });
          setTipoAdicionar(null);
          setElementoSel(id);
          return;
        }
        if (tipoAdicionar === 'servico') {
          const id = `el-${Date.now()}`;
          let serviceDeclarationId;
          try {
            serviceDeclarationId = createServiceDeclarationId();
          } catch {
            setMsg('Não foi possível gerar a identidade do serviço. Tente novamente.');
            return;
          }
          setElementos(prev => [...prev, {
            id, tipo: 'servico', serviceDeclarationId,
            nome: JSON.stringify({ tipo: 'servico', nomeDisplay: 'Serviço', serviceKey: '', nomeServico: '', descricao: '' }),
            x: pos.x - 40, y: pos.y - 40,
            width: 80, height: 80, rotation: 0,
          }]);
          setTipoAdicionar(null);
          setElementoSel(id);
          return;
        }
        if (tipoAdicionar === 'estacionamento') {
          const id = `el-${Date.now()}`;
          let serviceDeclarationId;
          try {
            serviceDeclarationId = createServiceDeclarationId();
          } catch {
            setMsg('Não foi possível gerar a identidade do serviço. Tente novamente.');
            return;
          }
          setElementos(prev => [...prev, {
            id, tipo: 'estacionamento', serviceDeclarationId,
            nome: JSON.stringify({ tipo: 'estacionamento', serviceKey: 'PARKING', description: 'Estacionamento' }),
            x: pos.x - 60, y: pos.y - 25,
            width: 120, height: 50, rotation: 0,
          }]);
          setTipoAdicionar(null);
          setElementoSel(id);
          return;
        }
        const tipo = TIPOS_ELEMENTO.find(t => t.tipo === tipoAdicionar);
        if (!tipo) return;
        const id = `el-${Date.now()}`;
        const secao = encontrarSecao(pos, secoes);
        const secaoNome = secao?.nome || '';
        setElementos(prev => [...prev, {
          id,
          tipo: tipoAdicionar,
          nome: rotuloElementoPorSecao(tipoAdicionar, secaoNome),
          secao: secaoNome,
          secaoId: sectionId(secao),
          x: pos.x - tipo.w / 2,
          y: pos.y - tipo.h / 2,
          width: tipo.w,
          height: tipo.h,
          rotation: 0,
        }]);
        setTipoAdicionar(null);
        setElementoSel(id);
      } else {
        if (e.target === e.target.getStage()) {
          setElementoSel(null);
        }
      }
    }

    if (modo === 'secoes') {
      setPerimetroSel(false);
      setElementoSel(null);
      setRotaSel(null);
      if (!secaoEmDesenho) return;
      const pts = secaoEmDesenho.pontos;
      if (pts.length >= 3 && distanceBetweenPoints(pos, pts[0]) < DIST_FECHAR) {
        const nova = { ...secaoEmDesenho };
        setSecoes(prev => [...prev, nova]);
        setSecaoSel(nova.id);
        setSecaoEmDesenho(null);
        abrirEdicaoSecao(nova, { nova: true });
        return;
      }
      const point = applySectionPointPolicy(pos, {
        shiftKey: e.evt.shiftKey,
        secoes,
      });
      setSecaoEmDesenho(prev => ({ ...prev, pontos: [...prev.pontos, point] }));
      return;
    }

    if (modo === 'rota') {
      setPerimetroSel(false);
      setElementoSel(null);
      setSecaoSel(null);
      // só adiciona ao clicar no fundo (não em marcadores existentes)
      if (e.target !== e.target.getStage()) return;
      const isShift = e.evt.shiftKey;
      const point = applyEditorPointPolicy(pos, {
        shiftKey: isShift,
        enableWeld: false,
      });
      const { x, y } = point;
      const secao = encontrarSecao(point, secoes);
      const secaoNome = secao?.nome || '';
      setRotaSel(rota.length);
      setRota(prev => [...prev, createRouteStop({
        position: { x, y },
        sectionName: secaoNome,
        sectionId: sectionId(secao),
      })]);
      return;
    }
  }

  // ── Stage mouse move — preview da rota ───────────────────────────────────
  function handleStageMouseMove(e) {
    if (editorLockedByProcessing || modo !== 'rota') return;
    const stage = e.target.getStage();
    if (!stage) return;
    setCursorPos(getPointerLogical(stage));
  }

  // ── Elemento: transform end ───────────────────────────────────────────────
  function onTransformEnd() {
    if (!ensureEditorWritable()) return;
    const id = elementoSel;
    if (!id) return;
    const node = elementRefs.current[id];
    if (!node) return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    setElementos(prev => prev.map(el =>
      el.id !== id
        ? el
        : atualizarPertencimentoElemento(el, secoes, {
            x: node.x(),
            y: node.y(),
            width: Math.max(20, el.width * scaleX),
            height: Math.max(20, el.height * scaleY),
            rotation: node.rotation(),
          })
    ));
  }

  function rotacionar90() {
    if (!ensureEditorWritable()) return;
    if (!elementoSel) return;
    setElementos(prev => prev.map(el =>
      el.id !== elementoSel ? el : { ...el, rotation: (el.rotation + 90) % 360 }
    ));
  }

  function removerSelecionado() {
    if (!ensureEditorWritable()) return;
    if (!elementoSel) return;
    setElementos(prev => prev.filter(el => el.id !== elementoSel));
    setElementoSel(null);
  }

  function duplicarSelecionado() {
    if (!ensureEditorWritable()) return;
    if (!elementoSel) return;
    const original = elementos.find(el => el.id === elementoSel);
    if (!original) return;
    if (original.tipo === 'entrada') return;
    if (original.tipo === 'servico') return;
    const novoId = `el-${Date.now()}`;
    const copia = atualizarPertencimentoElemento(
      { ...original, id: novoId },
      secoes,
      { x: original.x + 20, y: original.y + 20 }
    );
    setElementos(prev => [...prev, copia]);
    setElementoSel(novoId);
  }

  function trazerElementoParaFrente(id) {
    if (editorLockedByProcessing) return;
    setElementos(prev => {
      const el = prev.find(item => item.id === id);
      if (!el || prev[prev.length - 1]?.id === id) return prev;
      return [...prev.filter(item => item.id !== id), el];
    });
  }

  function salvarEdicaoElemento(semanticHints, aspects) {
    if (!ensureEditorWritable()) return;
    setItemAspectDrafts(previous => [...previous.filter(item => item.elementId !== editandoNome), ...aspects]);

    setElementos(prev => prev.map(el => {
      if (el.id !== editandoNome) return el;
      const secaoNome = el.secao || encontrarSecao(centroElemento(el), secoes)?.nome || '';
      const next = atualizarPertencimentoElemento({
        ...el,
        nome: rotuloElementoPorSecao(el.tipo, secaoNome),
        secao: secaoNome,
      }, secoes);
      if (semanticHints.length > 0) {
        next.semanticHints = semanticHints;
      } else {
        delete next.semanticHints;
      }
      return next;
    }));
    setEditandoNome(null);
  }

  function abrirEdicaoServico(el) {
    if (!ensureEditorWritable()) return;
    setEditandoNome(null); setElementoSel(el.id); setModo('elementos');
    const serviceDeclarationId = Object.prototype.hasOwnProperty.call(el, 'serviceDeclarationId')
      ? el.serviceDeclarationId
      : null;
    let dados = {
      nomeDisplay: 'Serviço',
      serviceKey: '',
      nomeServico: '',
      descricao: '',
      serviceDeclarationId,
    };
    try {
      const p = JSON.parse(el.nome);
      dados = {
        nomeDisplay:  p.nomeDisplay  || 'Serviço',
        serviceKey:   p.serviceKey   || '',
        nomeServico:  p.nomeServico  || '',
        descricao:    p.descricao    || '',
        serviceDeclarationId,
      };
    } catch {}
    setEditandoServico(el.id);
    setServiceEdited(false);
    setServicoTemp(dados);

  }

  function abrirEdicaoSecao(s, { nova = false } = {}) {
    setRenomeandoSecao(s.id);
    setSecaoNovaPendenteId(nova ? s.id : null);
    setNomeSecaoTemp(s.nome || '');
    setDiasTemp(s.dias || [0,1,2,3,4,5,6]);
    setPesoSecaoTemp(s.peso || 5);
    setSensivelSecaoTemp(s.sensivel === true);
  }

  function cancelarEdicaoSecao() {
    if (!editorLockedByProcessing && secaoNovaPendenteId && secaoNovaPendenteId === renomeandoSecao) {
      setSecoes(prev => prev.filter(section => section.id !== secaoNovaPendenteId));
      setSecaoSel(null);
    }
    setSecaoNovaPendenteId(null);
    setRenomeandoSecao(null);
  }

  function salvarEdicaoSecao() {
    if (!ensureEditorWritable()) return;
    if (cadastroSecaoInvalido) return;
    const nome = nomeSecaoTemp.trim();
    setSecoes(prev => prev.map(s => s.id === renomeandoSecao
      ? (() => {
          const liveSection = {
            ...s,
            nome,
            dias: diasTemp,
            peso: pesoSecaoTemp,
            sensivel: sensivelSecaoTemp === true,
          };
          delete liveSection.categoria;
          return liveSection;
        })()
      : s));
    setSecaoNovaPendenteId(null);
    setRenomeandoSecao(null);
  }

  function elementoEditavelParaSugestao(el) {
    return el && el.tipo !== 'entrada' && el.tipo !== 'servico';
  }

  function exemplosDaSecao(sectionReport, limit = 5) {
    return (Array.isArray(sectionReport?.feedback?.examples)
      ? sectionReport.feedback.examples
      : [])
      .map(example => String(example || '').trim())
      .filter(Boolean)
      .slice(0, limit);
  }

  function resolverAlvoSugestao(sectionReport, elementosFonte = elementos, preferirSelecionado = true) {
    const sectionName = sectionReport?.sectionName || '';
    const targetSectionKey = String(sectionReport?.sectionKey || '').trim();
    const targetSectionId = String(sectionReport?.sectionId || sectionReport?.secaoId || '').trim();
    const nameMatches = (targetSectionKey || targetSectionId)
      ? []
      : secoes.filter(s => sameLabel(s.nome, sectionName));
    const secao = targetSectionKey
      ? secoes.find(s => sectionIdentityKey(s) === targetSectionKey)
      : targetSectionId
        ? secoes.find(s => String(s.id || '').trim() === targetSectionId)
        : nameMatches.length === 1
          ? nameMatches[0]
          : null;
    if (!secao) return { secao: null, alvo: null };
    if (targetSectionId && sectionId(secao) !== targetSectionId) return { secao: null, alvo: null };
    if (sectionName && !sameLabel(secao.nome, sectionName)) return { secao: null, alvo: null };

    const targetIds = Array.isArray(sectionReport?.suggestionTargetElementIds)
      ? sectionReport.suggestionTargetElementIds
      : [];
    const fallbackTargetId = sectionReport?.suggestionTargetElementId || targetIds[0] || null;
    const targetIdSet = new Set(targetIds);
    const duplicateSectionName = secoes.filter(s => sameLabel(s.nome, secao.nome)).length > 1;
    const elementosCandidatos = elementosFonte.filter(el => {
      if (!elementoEditavelParaSugestao(el)) return false;
      const targetId = sectionId(secao);
      const elementSectionId = String(el?.secaoId || el?.sectionId || '').trim();
      if (targetId) {
        if (elementSectionId && elementSectionId !== targetId) return false;
        if (!elementSectionId && (duplicateSectionName || !sameLabel(el.secao, secao.nome))) return false;
      } else if (!sameLabel(el.secao, secao.nome)) return false;
      if (targetIdSet.size > 0) return targetIdSet.has(el.id);
      return true;
    });
    const elementoSelecionado = preferirSelecionado
      ? elementosCandidatos.find(el => el.id === elementoSel)
      : null;
    const primeiroElementoDaSecao =
      elementosCandidatos.find(el => fallbackTargetId && el.id === fallbackTargetId) ||
      elementosCandidatos[0];

    return { secao, alvo: elementoSelecionado || primeiroElementoDaSecao || null };
  }

  function elementoComSugestoes(elemento, secao, examples) {
    const secaoNome = canonicalSectionName(secao);
    const secaoIdentity = sectionId(secao);
    let semanticHints = normalizeSemanticHints(elemento?.semanticHints);
    let appliedHints = 0;

    for (const example of examples) {
      const beforeCount = semanticHints.length;
      semanticHints = semanticHintsWithSuggestion(semanticHints, example);
      if (semanticHints.length > beforeCount) appliedHints += 1;
    }

    const changed =
      !sameLabel(elemento?.secao, secaoNome) ||
      String(elemento?.secaoId || '') !== secaoIdentity ||
      JSON.stringify(semanticHints) !== JSON.stringify(normalizeSemanticHints(elemento?.semanticHints));

    return {
      changed,
      appliedHints,
      element: {
        ...elemento,
        secao: secaoNome,
        secaoId: secaoIdentity,
        semanticHints,
      },
    };
  }

  function marcarSugestoesAplicadas(detail) {
    if (!ensureEditorWritable()) return;
    setPlantSaveState('dirty');
    setProcessingDetail(detail || 'Sugestões aplicadas; salve para revalidar');
  }

  function aplicarExemploNaSecao(sectionReport, example) {
    if (!ensureEditorWritable()) return;
    const { secao, alvo } = resolverAlvoSugestao(sectionReport, elementos, true);
    if (!secao) {
      setMsg('Não encontrei a seção desta sugestão no mapa.');
      setTimeout(() => setMsg(''), 3000);
      return;
    }

    if (!alvo) {
      setModo('elementos');
      setSecaoSel(secao.id);
      setMsg(`Adicione ou posicione uma gôndola/ilha dentro de ${secao.nome} antes de usar "${example}".`);
      setTimeout(() => setMsg(''), 4500);
      return;
    }

    const normalizedExample = normalizeForCompare(example);
    const alreadyApplied = Array.isArray(alvo.semanticHints) &&
      normalizeSemanticHints(alvo.semanticHints).some(item => normalizeForCompare(item) === normalizedExample) &&
      sameLabel(alvo.secao, secao.nome);
    const nextTarget = elementoComSugestoes(alvo, secao, [example]);

    setElementos(prev => prev.map(el =>
      el.id !== alvo.id
        ? el
        : nextTarget.element
    ));
    setElementoSel(alvo.id);
    setModo('elementos');
    if (!alreadyApplied) {
      marcarSugestoesAplicadas('Sugestão aplicada; salve para revalidar');
    }
    setMsg(alreadyApplied
      ? `Sugestão já estava aplicada em ${secao.nome}: ${example}`
      : `Sugestão aplicada em ${secao.nome}: ${example}. Salve para revalidar.`
    );
    setTimeout(() => setMsg(''), 3000);
  }

  function aplicarSugestoesEmLote(sectionReports, { preferirSelecionado = false } = {}) {
    if (!ensureEditorWritable()) return;
    const reports = (Array.isArray(sectionReports) ? sectionReports : [])
      .filter(section => exemplosDaSecao(section).length > 0);

    if (reports.length === 0) {
      setMsg('Não há sugestões aplicáveis agora.');
      setTimeout(() => setMsg(''), 3000);
      return;
    }

    let proximosElementos = elementos;
    let appliedHints = 0;
    let changedSections = 0;
    let skippedWithoutTarget = 0;
    let firstTargetId = null;

    for (const section of reports) {
      const examples = exemplosDaSecao(section);
      const { secao, alvo } = resolverAlvoSugestao(section, proximosElementos, preferirSelecionado);
      if (!secao || !alvo) {
        skippedWithoutTarget += 1;
        continue;
      }

      const nextTarget = elementoComSugestoes(alvo, secao, examples);
      if (!nextTarget.changed) continue;

      appliedHints += nextTarget.appliedHints;
      changedSections += 1;
      firstTargetId = firstTargetId || alvo.id;
      proximosElementos = proximosElementos.map(el =>
        el.id === alvo.id ? nextTarget.element : el
      );
    }

    if (changedSections === 0) {
      const detail = skippedWithoutTarget > 0
        ? `Nenhuma sugestão aplicada; ${skippedWithoutTarget} seção(ões) precisam de elemento físico.`
        : 'As sugestões já estavam aplicadas.';
      setMsg(detail);
      setTimeout(() => setMsg(''), 4000);
      return;
    }

    setElementos(proximosElementos);
    if (firstTargetId) setElementoSel(firstTargetId);
    setModo('elementos');
    marcarSugestoesAplicadas(`${appliedHints} sugestão(ões) aplicada(s); salve para revalidar`);

    const skippedText = skippedWithoutTarget > 0
      ? ` ${skippedWithoutTarget} seção(ões) ficaram sem alvo físico.`
      : '';
    setMsg(`${appliedHints} sugestão(ões) aplicada(s) em ${changedSections} seção(ões). Salve para revalidar.${skippedText}`);
    setTimeout(() => setMsg(''), 5000);
  }

  function aplicarNomeCanonico(sectionReport, canonicalName) {
    if (!ensureEditorWritable()) return;
    if (!canonicalName) return;
    const targetSectionKey = String(sectionReport?.sectionKey || '').trim();
    const targetSectionId = String(sectionReport?.sectionId || sectionReport?.secaoId || '').trim();
    const sectionName = sectionReport?.sectionName || '';
    const matches = targetSectionKey
      ? secoes.filter(secao => sectionIdentityKey(secao) === targetSectionKey)
      : targetSectionId
        ? secoes.filter(secao => sectionId(secao) === targetSectionId)
        : secoes.filter(secao => sameLabel(secao.nome, sectionName));
    const target = matches.length === 1 ? matches[0] : null;
    if (!target || (sectionName && !sameLabel(target.nome, sectionName))) {
      setMsg('Não foi possível identificar uma única seção para aplicar esta sugestão.');
      setTimeout(() => setMsg(''), 3500);
      return;
    }
    const targetIdentity = sectionIdentityKey(target);
    setSecoes(prev => prev.map(secao =>
      sectionIdentityKey(secao) === targetIdentity
        ? { ...secao, nome: canonicalName }
        : secao
    ));
    setModo('secoes');
    setMsg(`Sugestão aplicada: ${canonicalName}`);
    setTimeout(() => setMsg(''), 2500);
  }

  // ── Estilos ───────────────────────────────────────────────────────────────
  const toolbarModes = [
    ['perimetro', 'Perímetro'],
    ['elementos', 'Elementos'],
    ['secoes', 'Seções'],
    ['rota', 'Rota'],
  ];
  const uiToneFromColor = cor => {
    if (cor === '#ef4444') return { bg: 'var(--error-subtle)', border: 'var(--error)', color: 'var(--error)' };
    if (cor === '#f59e0b') return { bg: 'var(--warning-subtle)', border: 'var(--warning)', color: 'var(--warning)' };
    if (cor === '#22c55e' || cor === '#166534') return { bg: 'var(--success-subtle)', border: 'var(--success)', color: 'var(--success)' };
    return { bg: 'var(--action-subtle)', border: 'var(--action-border)', color: 'var(--text-primary)' };
  };
  const btn = (cor = '#3b82f6', disabled = false) => ({
    padding: '4px 8px',
    borderRadius: 4,
    border: `1px solid ${disabled ? 'var(--border)' : uiToneFromColor(cor).border}`,
    cursor: disabled ? 'default' : 'pointer',
    background: disabled ? 'var(--bg-surface)' : uiToneFromColor(cor).bg,
    color: disabled ? 'var(--text-muted)' : uiToneFromColor(cor).color,
    fontSize: 12,
    fontWeight: 650,
    lineHeight: 1.15,
    opacity: disabled ? 0.6 : 1,
    whiteSpace: 'nowrap',
  });

  function dadosEntradaElemento(el) {
    if (el?.tipo !== 'entrada') return null;
    try {
      const parsed = JSON.parse(el.nome || '{}');
      return parsed?.tipo === 'entrada' ? parsed : null;
    } catch {
      return null;
    }
  }

  function nomePadraoNovaEntrada(elementosAtuais) {
    const entradas = elementosAtuais.filter(el => el.tipo === 'entrada');
    if (entradas.length === 0) return 'Entrada Principal';

    const maiorIndice = entradas.reduce((maior, el, index) => {
      const dados = dadosEntradaElemento(el);
      const nome = String(dados?.nomeDisplay || '').trim();
      const match = nome.match(/^entrada\s+(\d+)$/i);
      if (match) return Math.max(maior, Number(match[1]));
      if (/^entrada principal$/i.test(nome)) return Math.max(maior, 1);
      return Math.max(maior, index + 1);
    }, 0);

    return `Entrada ${Math.max(maiorIndice + 1, entradas.length + 1)}`;
  }

  function aplicarCorrecaoOrtografica(correction) {
    if (!ensureEditorWritable()) return;
    const elementId = String(correction?.elementId || '').trim();
    const rawText = String(correction?.rawText || '').trim();
    const suggestedText = String(correction?.suggestedText || '').trim();
    if (
      !elementId
      || correction?.sourceField !== 'semanticHints'
      || !rawText
      || !suggestedText
      || rawText === suggestedText
    ) {
      setMsg('Abra o elemento e revise manualmente esta correção.');
      setTimeout(() => setMsg(''), 3500);
      return;
    }

    let changed = false;
    const nextElements = elementos.map(elemento => {
      if (String(elemento?.id || elemento?.elementId || '').trim() !== elementId) return elemento;
      const semanticHints = (Array.isArray(elemento?.semanticHints) ? elemento.semanticHints : [])
        .map(item => {
          const value = String(item || '').trim();
          if (value !== rawText) return item;
          changed = true;
          return suggestedText;
        });
      return changed ? { ...elemento, semanticHints: normalizeSemanticHints(semanticHints) } : elemento;
    });

    if (!changed) {
      setMsg('A mercadoria mudou; abra o elemento e revise a grafia atual.');
      setTimeout(() => setMsg(''), 3500);
      return;
    }

    setElementos(nextElements);
    setPendingOrthographyCorrections(current => current.filter(item => !(
      String(item?.elementId || '').trim() === elementId
      && item?.sourceField === correction.sourceField
      && item?.rawText === correction.rawText
      && item?.ruleId === correction.ruleId
    )));
    setPlantSaveState('dirty');
    setProcessingDetail('Ortografia corrigida; conclua para salvar a planta');
    setSemanticAdviceRefreshSeq(value => value + 1);
    setMsg(`Correção aplicada: ${rawText} → ${suggestedText}.`);
    setTimeout(() => setMsg(''), 3000);
  }

  function salvarParadaModal() {
    if (!ensureEditorWritable()) return;
    const relation = String(paradaModalTemp.relation || '').trim();
    const targetKey = String(paradaModalTemp.targetKey || '').trim();
    if (Boolean(relation) !== Boolean(targetKey)) return;
    let target = null;
    if (targetKey) {
      try {
        target = JSON.parse(targetKey);
      } catch {
        return;
      }
    }
    setRota(prev => prev.map((stop, index) => {
      if (index !== editandoParadaModal) return stop;
      return applyRouteStopTypedEdit(stop, {
        direita: paradaModalTemp.direita,
        esquerda: paradaModalTemp.esquerda,
        frente: paradaModalTemp.frente,
        relation,
        target,
      });
    }));
    setEditandoParadaModal(null);
  }

  const perimFlat = perimetro.flatMap(p => [p.x, p.y]);

  // ── Zoom + pan helpers ────────────────────────────────────────────────────
  function getPointerLogical(stage) {
    const pointer = stage.getPointerPosition();
    const scale = stage.scaleX();
    return {
      x: (pointer.x - stage.x()) / scale,
      y: (pointer.y - stage.y()) / scale,
    };
  }

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    stage.scale({ x: clampedScale, y: clampedScale });
    stage.position({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
    stage.batchDraw();
    setPlantView({ x: stage.x(), y: stage.y(), scale: clampedScale });
  };

  // Seção onde o cursor está (aba Rota)
  const secaoCursor = (modo === 'rota' && secoes.length > 0 && cursorPos)
    ? encontrarSecao(cursorPos, secoes)
    : null;

  const reprocessPreview = useMemo(() => buildReprocessPreview(
    lastSavedPlantRef.current,
    { perimetro, elementos, secoes, rota }
  ), [perimetro, elementos, secoes, rota]);
  const reprocessChangedSectionSet = useMemo(() => new Set(
    (reprocessPreview.changedSections || []).map(sectionKey)
  ), [reprocessPreview]);
  const reprocessSectionText = reprocessPreview.changedSections?.join(', ') || '';
  const hasPlantDraft = perimetro.length > 0 || elementos.length > 0 || secoes.length > 0 || rota.length > 0;
  const minimumPlantReady = minimumOperationalPlant?.ready === true;
  const operationalDiversityReady = operationalSectionDiversity?.ready === true;
  const plantAdmissionReady = minimumPlantReady && operationalDiversityReady;
  const canRequestSectionReprocess = plantSaveState === 'dirty' && reprocessPreview.scope === 'SECTION';
  const primaryPlantActionDisabled = saveDisabled || !clienteId || !hasPlantDraft || !plantAdmissionReady || plantSaveState !== 'dirty';
  const manualFullReprocessDisabled = saveDisabled || !clienteId || !hasPlantDraft || !plantAdmissionReady;
  const hasDirtyReprocessPreview = plantSaveState === 'dirty'
    && (reprocessPreview.scope === 'SECTION' || reprocessPreview.scope === 'FULL');
  const primaryPlantActionLabel = salvando
    ? 'Salvando...'
    : plantSaveState === 'dirty'
      ? (canRequestSectionReprocess ? 'Salvar e revalidar seção' : 'Salvar e validar planta')
      : 'Salvar alterações';
  const primaryPlantActionTitle = canRequestSectionReprocess
      ? `${reprocessSectionText || 'A seção alterada'} será revalidada após salvar.`
      : plantSaveState === 'dirty'
        ? plantAdmissionReady
          ? 'Salva o rascunho atual e atualiza a validação determinística da planta.'
          : 'Complete os itens obrigatórios da planta mínima mostrados no assistente semântico.'
        : 'Edite a planta para habilitar o save.';
  const sectionReprocessActionTitle = canRequestSectionReprocess
      ? `${reprocessSectionText || 'A seção alterada'} será revalidada após salvar.`
      : plantSaveState === 'dirty'
        ? 'A alteração atual exige validação da planta completa.'
        : 'Edite uma seção para habilitar a revalidação parcial.';
  const manualFullReprocessTitle = 'Ação avançada: revalida deterministicamente a planta inteira. Não gera candidate nem consome IA.';
  const routeSaveActionTitle = canRequestSectionReprocess
    ? sectionReprocessActionTitle
    : primaryPlantActionTitle;
  const routeSaveActionLabel = salvando
    ? 'Salvando...'
    : plantSaveState === 'dirty' && reprocessPreview.scope === 'SECTION'
      ? 'Concluir e revalidar seção'
      : plantSaveState === 'dirty' && reprocessPreview.scope === 'FULL'
        ? 'Concluir e validar planta'
        : 'Concluir rota';

  async function handlePrimaryPlantAction() {
    if (primaryPlantActionDisabled) return;
    await salvar(canRequestSectionReprocess ? 'SECTION' : 'AUTO');
  }

  async function handleManualFullReprocess() {
    if (manualFullReprocessDisabled) return;
    const sectionCount = secoes.length;
    const ok = await confirm({
      title: 'Revalidar planta inteira?',
      message: `Esta ação recalcula o contexto determinístico das ${sectionCount} seção(ões). Não gera candidate, não abre revisão e não consome IA.`,
      danger: false,
      confirmLabel: 'Revalidar tudo',
      cancelLabel: 'Cancelar',
      confirmationText: 'REVALIDAR',
      confirmationLabel: 'Digite REVALIDAR para confirmar a validação completa.',
    });
    if (!ok) return;
    await salvar('FULL');
  }

  const advisorSections = Array.isArray(semanticAdvice?.sections) ? semanticAdvice.sections : [];
  const minimumPlantIssues = Array.isArray(minimumOperationalPlant?.issues)
    ? minimumOperationalPlant.issues
    : [];
  const operationalDiversityIssues = Array.isArray(operationalSectionDiversity?.issues)
    ? operationalSectionDiversity.issues
    : [];
  const operationalSectionsActual = Number.isInteger(operationalSectionDiversity?.eligibleSectionCount)
    ? operationalSectionDiversity.eligibleSectionCount
    : 0;
  const operationalSectionsRequired = Number.isInteger(operationalSectionDiversity?.minimumRequiredSections)
    ? operationalSectionDiversity.minimumRequiredSections
    : 3;
  const incompleteOperationalSections = Array.isArray(operationalSectionDiversity?.sectionCoverage)
    ? operationalSectionDiversity.sectionCoverage.filter(section => (
      section?.mentionableItemReady !== true || section?.canonicalRouteStopReady !== true
    ))
    : [];
  const minimumPlantChecklist = [
    ['Perímetro', minimumOperationalPlant?.summary?.perimeterReady === true],
    ['Seções', minimumOperationalPlant?.summary?.sectionsReady === true],
    ['Mercadoria', minimumOperationalPlant?.summary?.merchandiseReady === true],
    ['Rota', minimumOperationalPlant?.summary?.routeReady === true],
    ['Cadeia factual', minimumOperationalPlant?.summary?.completeChainReady === true],
    [
      `Seções operacionais ${operationalSectionsActual}/${operationalSectionsRequired}`,
      operationalDiversityReady,
    ],
  ];
  const advisorOrthographyCorrections = advisorSections.flatMap(section =>
    Array.isArray(section?.orthographyCorrections) ? section.orthographyCorrections : []
  );
  const visibleOrthographyCorrections = [
    ...pendingOrthographyCorrections,
    ...advisorOrthographyCorrections,
  ].filter(correction => (
    correction?.sourceField !== 'semanticHints'
    || elementos.some(elemento => (
      String(elemento?.id || elemento?.elementId || '').trim()
        === String(correction?.elementId || '').trim()
      && normalizeSemanticHints(elemento?.semanticHints)
        .some(value => value === correction?.rawText)
    ))
  )).filter((correction, index, values) => values.findIndex(candidate => (
    String(candidate?.elementId || '') === String(correction?.elementId || '')
    && candidate?.sourceField === correction?.sourceField
    && candidate?.ruleId === correction?.ruleId
    && candidate?.rawText === correction?.rawText
  )) === index);
  function renderSemanticAdvisorPanel() {
    return (
      <div style={{
        background: 'transparent',
        border: 0,
        borderRadius: 0,
        padding: 0,
        color: 'var(--text-primary)',
      }}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 13 }}>Dados da planta</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
            Verificação dos dados necessários para salvar
          </div>
        </div>

        {minimumOperationalPlant && (
          <div style={{
            border: `1px solid ${plantAdmissionReady ? 'var(--success)' : 'var(--warning)'}`,
            background: plantAdmissionReady ? 'var(--success-subtle)' : 'var(--warning-subtle)',
            borderRadius: 4,
            padding: 7,
            marginBottom: 8,
          }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 800, marginBottom: 5 }}>
              {plantAdmissionReady ? 'Planta pronta para salvar' : 'Complete os dados para salvar'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: plantAdmissionReady ? 0 : 5 }}>
              {minimumPlantChecklist.map(([label, ready]) => (
                <span key={label} style={{
                  border: `1px solid ${ready ? 'var(--success)' : 'var(--border)'}`,
                  borderRadius: 999,
                  color: ready ? 'var(--success)' : 'var(--text-secondary)',
                  background: 'var(--bg-base)',
                  padding: '2px 6px',
                  fontSize: 10,
                  fontWeight: 700,
                }}>
                  {ready ? '✓' : '○'} {label}
                </span>
              ))}
            </div>
            {!minimumPlantReady && minimumPlantIssues.slice(0, 4).map(item => (
              <div key={`${item.code}:${item.field}`} style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.35 }}>
                • {item.message}
              </div>
            ))}
            {minimumPlantReady && !operationalDiversityReady && operationalDiversityIssues.slice(0, 4).map(item => (
              <div key={`${item.code}:${item.field}`} style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.35 }}>
                • {item.message}
              </div>
            ))}
            {minimumPlantReady && !operationalDiversityReady && incompleteOperationalSections.slice(0, 4).map(section => {
              const missing = [
                section?.mentionableItemReady === true ? null : 'mercadoria',
                section?.canonicalRouteStopReady === true ? null : 'parada ROUTE identificada',
              ].filter(Boolean).join(' e ');
              return (
                <div key={`coverage:${section.sectionId}`} style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.35 }}>
                  • {section.sectionName || section.sectionId}: falta {missing}
                </div>
              );
            })}
          </div>
        )}

        {visibleOrthographyCorrections.map(correction => (
          <div key={`${correction.elementId}:${correction.sourceField}:${correction.ruleId}:${correction.rawText}`} style={{
            border: '1px solid var(--warning)',
            background: 'var(--warning-subtle)',
            borderRadius: 4,
            padding: 6,
            marginBottom: 6,
          }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 12, lineHeight: 1.35 }}>
              Revise o nome informado: <strong>{correction.rawText}</strong> → <strong>{correction.suggestedText}</strong>
            </div>
            <button
              type="button"
              onClick={() => aplicarCorrecaoOrtografica(correction)}
              disabled={editorLockedByProcessing || correction.sourceField !== 'semanticHints'}
              title={correction.sourceField === 'semanticHints'
                ? 'Aplicar correção antes de salvar'
                : 'Abra o elemento para revisar este campo'}
              style={{
                marginTop: 5,
                padding: '3px 7px',
                borderRadius: 4,
                border: '1px solid var(--warning)',
                background: 'transparent',
                color: 'var(--warning)',
                cursor: correction.sourceField === 'semanticHints' ? 'pointer' : 'default',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {correction.sourceField === 'semanticHints' ? 'Corrigir antes de salvar' : 'Revisar no elemento'}
            </button>
          </div>
        ))}

        {hasDirtyReprocessPreview && (
          <div style={{
            border: `1px solid ${reprocessPreview.scope === 'SECTION' ? 'var(--action-border)' : 'var(--warning)'}`,
            background: reprocessPreview.scope === 'SECTION' ? 'var(--action-subtle)' : 'var(--warning-subtle)',
            color: 'var(--text-primary)',
            borderRadius: 4,
            padding: 6,
            marginBottom: 6,
            fontSize: 12,
            lineHeight: 1.35,
          }}>
            <strong style={{ display: 'block', fontSize: 12, marginBottom: 2 }}>
              {reprocessPreview.scope === 'SECTION'
                ? `${reprocessSectionText || 'A seção alterada'} será revalidada`
                : 'A planta inteira será revalidada'}
            </strong>
            <span style={{ color: 'var(--text-secondary)' }}>
              {reprocessPreview.scope === 'SECTION'
                ? 'As demais seções permanecem fora da revalidação local.'
                : 'A validação completa será atualizada.'}
            </span>
          </div>
        )}

        {plantSaveState === 'saved' && (
          <div style={{
            border: '1px solid var(--success)',
            background: 'var(--success-subtle)',
            color: 'var(--text-primary)',
            borderRadius: 4,
            padding: 6,
            marginBottom: 6,
            fontSize: 12,
            lineHeight: 1.35,
          }}>
            Planta salva e validação atualizada.
          </div>
        )}

        {plantSaveState === 'saved_warning' && (
          <div style={{
            border: '1px solid var(--warning)',
            background: 'var(--warning-subtle)',
            color: 'var(--text-primary)',
            borderRadius: 4,
            padding: 6,
            marginBottom: 6,
            fontSize: 12,
            lineHeight: 1.35,
          }}>
            A planta foi salva, mas a validação ainda está sendo concluída.
          </div>
        )}

        {!minimumOperationalPlant && !semanticAdviceLoading && (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.45 }}>
            Desenhe a planta para verificar os dados necessários para salvar.
          </div>
        )}
      </div>
    );
  }

  function renderSectionsInspectorPanel() {
    return (
      <div style={{
        background: 'transparent',
        border: 0,
        borderRadius: 0,
        padding: 0,
        color: 'var(--text-primary)',
      }}>
        <div style={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 13, marginBottom: 6 }}>
          Seções ({secoes.length})
        </div>
        {secaoEmDesenho && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 6px', borderRadius: 4,
            background: 'var(--action-subtle)', marginBottom: 4,
            border: '1px solid var(--action-border)',
          }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: secaoEmDesenho.cor, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-primary)', fontSize: 13, flex: 1 }}>{secaoEmDesenho.nome}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>em desenho</span>
          </div>
        )}
        {secoes.length === 0 && !secaoEmDesenho && (
          <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: '1.35' }}>
            Nenhuma seção criada.<br />Clique em "+ Nova Seção".
          </div>
        )}
        {secoes.map(s => {
          const willReprocessSection = plantSaveState === 'dirty'
            && reprocessPreview.scope === 'SECTION'
            && reprocessChangedSectionSet.has(sectionKey(s.nome));
          return (
            <div
              key={s.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 6px', borderRadius: 4, cursor: 'pointer', marginBottom: 2,
                background: secaoSel === s.id || willReprocessSection ? 'var(--action-subtle)' : 'transparent',
                border: `1px solid ${secaoSel === s.id || willReprocessSection ? 'var(--action-border)' : 'transparent'}`,
              }}
              onClick={() => setSecaoSel(secaoSel === s.id ? null : s.id)}
            >
              <div style={{ width: 10, height: 10, borderRadius: 2, background: s.cor, flexShrink: 0 }} />
              <span style={{ color: 'var(--text-primary)', fontSize: 13, flex: 1 }}>{s.nome}</span>
              {willReprocessSection && (
                <span style={{
                  color: 'var(--action)',
                  background: 'var(--action-subtle)',
                  border: '1px solid var(--action-border)',
                  borderRadius: 4,
                  padding: '1px 4px',
                  fontSize: 10,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}>
                  revalidar
                </span>
              )}
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.pontos.length}pts</span>
            </div>
          );
        })}
      </div>
    );
  }

  function renderMapSidebar() {
    const sidebarTitle = inspectorTab === 'secoes' ? 'Seções da planta' : 'Dados da planta';
    const sidebarSubtitle = inspectorTab === 'secoes'
      ? `${secoes.length} seção(ões) desenhadas`
      : minimumPlantReady
        ? 'Dados mínimos completos'
        : 'Complete os dados obrigatórios';

    const renderAccordionHeader = (tab, label, metric, helper) => (
      <button
        type="button"
        className={`plant-editor-sidebar-accordion-button ${inspectorTab === tab ? 'is-active' : ''}`}
        aria-expanded={inspectorOpen && inspectorTab === tab}
        onClick={() => {
          setInspectorTab(tab);
          setInspectorOpen(true);
        }}
      >
        <span>
          <strong>{label}</strong>
          <small>{helper}</small>
        </span>
        <span className="plant-editor-sidebar-metric">{metric}</span>
      </button>
    );

    return (
      <aside
        className={`plant-editor-map-sidebar ${inspectorOpen ? 'is-open' : 'is-collapsed'}`}
        aria-label="Painel do mapa da loja"
      >
        {!inspectorOpen ? (
          <div className="plant-editor-sidebar-rail">
            <button
              type="button"
              className={`plant-editor-sidebar-rail-button ${inspectorTab === 'secoes' ? 'is-active' : ''}`}
              onClick={() => {
                setInspectorTab('secoes');
                setInspectorOpen(true);
              }}
              title="Abrir seções"
            >
              <span>Seç.</span>
              <strong>{secoes.length}</strong>
            </button>
            <button
              type="button"
              className={`plant-editor-sidebar-rail-button ${inspectorTab === 'semantica' ? 'is-active' : ''}`}
              onClick={() => {
                setInspectorTab('semantica');
                setInspectorOpen(true);
              }}
              title="Abrir dados da planta"
            >
              <span>Dados</span>
              <strong style={{ color: minimumPlantReady ? 'var(--success)' : 'var(--warning)' }}>
                {minimumPlantReady ? '✓' : '!'}
              </strong>
            </button>
            <button
              type="button"
              className="plant-editor-sidebar-toggle"
              onClick={() => setInspectorOpen(true)}
              aria-label="Abrir painel do mapa"
            >
              ›
            </button>
          </div>
        ) : (
          <div className="plant-editor-sidebar-body">
            <div className="plant-editor-sidebar-header">
              <div>
                <div className="plant-editor-sidebar-title">{sidebarTitle}</div>
                <div className="plant-editor-sidebar-subtitle">{sidebarSubtitle}</div>
              </div>
              <button
                type="button"
                className="plant-editor-sidebar-toggle"
                onClick={() => setInspectorOpen(false)}
                aria-label="Recolher painel do mapa"
              >
                ‹
              </button>
            </div>

            <div className="plant-editor-sidebar-accordion">
              {renderAccordionHeader('secoes', 'Seções', secoes.length, secaoEmDesenho ? 'em desenho' : 'desenhadas')}
              {inspectorTab === 'secoes' && (
                <div className="plant-editor-sidebar-accordion-panel">
                  {renderSectionsInspectorPanel()}
                </div>
              )}

              {renderAccordionHeader(
                'semantica',
                'Dados',
                minimumPlantReady ? '✓' : '!',
                minimumPlantReady ? 'prontos para salvar' : 'pendentes'
              )}
              {inspectorTab === 'semantica' && (
                <div className="plant-editor-sidebar-accordion-panel">
                  {renderSemanticAdvisorPanel()}
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    );
  }

  function plantStatusMeta() {
    if (authoring.phase === 'LOADING') return { label: 'Carregando planta', detail: 'Edição bloqueada até confirmar a revisão', tone: 'warning' };
    if (authoring.phase === 'LOAD_FAILED') return { label: 'Falha ao carregar', detail: 'Os dados não foram substituídos', tone: 'error' };
    if (authoring.phase === 'STALE_REVISION') return { label: 'Revisão desatualizada', detail: 'Rascunho preservado; recarregue para conferir a versão salva', tone: 'warning' };
    switch (plantSaveState) {
      case 'dirty':
        if (reprocessPreview.scope === 'SECTION') {
          return {
            label: 'Alteração local',
            detail: reprocessPreview.changedSections.length > 1
              ? `${reprocessPreview.changedSections.length} seções serão revalidadas`
              : `${reprocessSectionText || 'Seção'} será revalidada`,
            tone: 'action',
          };
        }
        if (reprocessPreview.scope === 'FULL') {
          return { label: 'Alteração ampla', detail: 'Planta será revalidada', tone: 'warning' };
        }
        return { label: 'Não salva', detail: 'Salve para validar', tone: 'warning' };
      case 'saving':
        return { label: 'Salvando', detail: 'Gravando estrutura', tone: 'action' };
      case 'saved':
        return { label: 'Planta salva', detail: processingDetail || 'Validação determinística atualizada', tone: 'success' };
      case 'saved_warning':
        return { label: 'Salva com pendência', detail: processingDetail || 'Contexto determinístico pendente', tone: 'warning' };
      case 'unchanged':
        return { label: 'Planta salva', detail: 'Sem alteração estrutural', tone: 'muted' };
      case 'error':
        return { label: 'Falha ao salvar', detail: 'Tente novamente', tone: 'error' };
      default:
        return { label: 'Carregada', detail: 'Edite e salve quando terminar', tone: 'muted' };
    }
  }

  function renderPlantStatus() {
    const meta = plantStatusMeta();
    return (
      <span
        className={`plant-editor-status-pill is-${meta.tone}`}
        title={meta.detail}
      >
        <span className="plant-editor-status-dot" />
        <span>{meta.label}</span>
        <span className="plant-editor-status-detail">{meta.detail}</span>
      </span>
    );
  }

  const gridLines = useMemo(() => {
    const lines = [];
    for (let x = 0; x <= MAP_W; x += 20) {
      const isPrimary = x % 100 === 0;
      lines.push({ key: `v-${x}`, points: [x, 0, x, MAP_H], stroke: isPrimary ? '#3a3a3a' : '#2a2a2a', strokeWidth: isPrimary ? 1 : 0.5 });
    }
    for (let y = 0; y <= MAP_H; y += 20) {
      const isPrimary = y % 100 === 0;
      lines.push({ key: `h-${y}`, points: [0, y, MAP_W, y], stroke: isPrimary ? '#3a3a3a' : '#2a2a2a', strokeWidth: isPrimary ? 1 : 0.5 });
    }
    return lines;
  }, []);

  const elementoEmEdicao = editandoNome
    ? elementos.find(el => el.id === editandoNome)
    : null;
  const rotuloAutomaticoEdicao = elementoEmEdicao
    ? rotuloElementoPorSecao(
        elementoEmEdicao.tipo,
        elementoEmEdicao.secao || encontrarSecao(centroElemento(elementoEmEdicao), secoes)?.nome || ''
      )
    : '';

  return (
    <div className="plant-editor-shell">
      {authoring.error && (
        <div role="alert" className="alert alert-error">
          {authoring.error}
          <button type="button" disabled={salvando} onClick={async () => {
            if (authoring.phase !== 'LOAD_FAILED' && lastSavedPlantSignatureRef.current !== plantSignature({ perimetro, elementos, secoes, rota })) {
              const discard = await confirm({ title: 'Recarregar planta', message: 'Recarregar descarta este rascunho e abre a versão salva. Deseja continuar?', confirmLabel: 'Descartar e recarregar', danger: true });
              if (!discard) return;
            }
            await authoring.load();
          }}>
            {authoring.phase === 'LOAD_FAILED' ? 'Tentar carregar novamente' : 'Recarregar versão salva'}
          </button>
        </div>
      )}
      {/* ── Toolbar principal ── */}
      <div className="plant-editor-commandbar">
        <div className="plant-editor-mode-tabs" role="tablist" aria-label="Modo de edição da planta">
          {toolbarModes.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`plant-editor-mode-tab ${modo === key ? 'is-active' : ''}`}
              aria-pressed={modo === key}
              onClick={() => {
                setModo(key);
                if (key !== 'secoes') setSecaoEmDesenho(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="plant-editor-toolbar-spacer" />
        {renderPlantStatus()}
        <div className="plant-editor-reprocess-actions" aria-label="Ações de validação da planta">
          <button
            type="button"
            className="plant-editor-save-button is-primary"
            onClick={handlePrimaryPlantAction}
            disabled={primaryPlantActionDisabled}
            title={primaryPlantActionTitle}
          >
            {primaryPlantActionLabel}
          </button>
          <details className="plant-editor-advanced-actions">
            <summary>Avançado</summary>
            <div className="plant-editor-advanced-menu">
              <button
                type="button"
                className="plant-editor-save-button is-danger"
                onClick={handleManualFullReprocess}
                disabled={manualFullReprocessDisabled}
                title={manualFullReprocessTitle}
              >
                Revalidar planta inteira
              </button>
              <span>
                Recalcula o contexto determinístico sem gerar candidate nem chamar IA.
              </span>
            </div>
          </details>
        </div>
      </div>

      {msg && (
        <div className={`plant-editor-message ${plantSaveState === 'error' || msg.startsWith('Erro') ? 'is-error' : 'is-info'}`}>
          {msg}
        </div>
      )}

      {/* ── Sub-toolbar Perímetro ── */}
      {modo === 'perimetro' && (
        <div className="plant-editor-contextbar">
          <span className="plant-editor-context-text">
            {fechado
              ? 'Polígono fechado'
              : perimetro.length === 0
                ? 'Clique no canvas para adicionar pontos'
                : `${perimetro.length} ponto(s) — clique próximo ao ponto verde para fechar`}
          </span>
          {!fechado && perimetro.length > 0 && (
            <button
              style={btn('#f59e0b', editorLockedByProcessing)}
              disabled={editorLockedByProcessing}
              onClick={() => { if (ensureEditorWritable()) setPerimetro(p => p.slice(0, -1)); }}
            >
              Desfazer
            </button>
          )}
          {perimetro.length > 0 && (
            <button
              style={btn('#ef4444', editorLockedByProcessing)}
              disabled={editorLockedByProcessing}
              onClick={() => {
                if (!ensureEditorWritable()) return;
                setPerimetro([]);
                setFechado(false);
              }}
            >
              Limpar
            </button>
          )}
        </div>
      )}

      {/* ── Sub-toolbar Elementos ── */}
      {modo === 'elementos' && (
        <div className="plant-editor-contextbar">
          {TIPOS_ELEMENTO.map(t => (
            <button
              key={t.tipo}
              disabled={editorLockedByProcessing}
              onClick={() => setTipoAdicionar(tipoAdicionar === t.tipo ? null : t.tipo)}
              style={{
                ...btn(t.cor, editorLockedByProcessing),
                border: tipoAdicionar === t.tipo ? '1px solid var(--action)' : '1px solid var(--border)',
                opacity: editorLockedByProcessing ? 0.6 : tipoAdicionar === t.tipo ? 1 : 0.75,
              }}
            >
              {tipoElementoLabel(t.tipo)}
            </button>
          ))}
          <button
            disabled={editorLockedByProcessing}
            onClick={() => setTipoAdicionar(tipoAdicionar === 'entrada' ? null : 'entrada')}
            style={{
              ...btn('#166534', editorLockedByProcessing),
              border: tipoAdicionar === 'entrada' ? '1px solid var(--success)' : '1px solid var(--border)',
              opacity: editorLockedByProcessing ? 0.6 : tipoAdicionar === 'entrada' ? 1 : 0.75,
              color: 'var(--success)',
            }}
          >
            Entrada
          </button>
          <button
            disabled={editorLockedByProcessing}
            onClick={() => setTipoAdicionar(tipoAdicionar === 'servico' ? null : 'servico')}
            style={{
              ...btn('#1e3a5f', editorLockedByProcessing),
              border: tipoAdicionar === 'servico' ? '1px solid var(--action)' : '1px solid var(--border)',
              opacity: editorLockedByProcessing ? 0.6 : tipoAdicionar === 'servico' ? 1 : 0.75,
              color: 'var(--text-primary)',
            }}
          >
            Serviço
          </button>
          <div className="plant-editor-context-divider" />
          {elementoSel && (
            <>
              <button style={btn('#6366f1', editorLockedByProcessing)} disabled={editorLockedByProcessing} onClick={rotacionar90}>Rotacionar</button>
              <button style={btn('#0ea5e9', editorLockedByProcessing)} disabled={editorLockedByProcessing} onClick={duplicarSelecionado}>⧉ Duplicar</button>
              <button style={btn('#ef4444', editorLockedByProcessing)} disabled={editorLockedByProcessing} onClick={removerSelecionado}>× Remover</button>
            </>
          )}
          {elementos.length > 0 && (
            <button
              style={btn('#ef4444', editorLockedByProcessing)}
              disabled={editorLockedByProcessing}
              onClick={() => {
                if (!ensureEditorWritable()) return;
                setElementos([]);
                setElementoSel(null);
              }}
            >
              Limpar tudo
            </button>
          )}
          {tipoAdicionar && (
            <span className="plant-editor-context-text is-action">Clique no canvas para posicionar</span>
          )}
        </div>
      )}

      {/* ── Sub-toolbar Seções ── */}
      {modo === 'secoes' && (
        <div className="plant-editor-contextbar">
          <button
            style={btn('#6366f1', !!secaoEmDesenho || editorLockedByProcessing)}
            disabled={!!secaoEmDesenho || editorLockedByProcessing}
            onClick={() => {
              if (!ensureEditorWritable()) return;
              const id  = `sec-${Date.now()}`;
              const cor = CORES_SECAO[secoes.length % CORES_SECAO.length];
              setSecaoEmDesenho({
                id,
                nome: '',
                cor,
                pontos: [],
                dias: [0,1,2,3,4,5,6],
                peso: 5,
                sensivel: false,
              });
              setSecaoSel(null);
            }}
          >
            + Nova Seção
          </button>
          <span className="plant-editor-context-text">
            A grade não move os vértices da seção. Shift desativa o encaixe em vértice vizinho.
          </span>
          {secaoEmDesenho && (
            <>
              <span className="plant-editor-context-text">
                {secaoEmDesenho.pontos.length === 0
                  ? 'Clique no canvas para adicionar pontos'
                  : `${secaoEmDesenho.pontos.length} ponto(s) — clique próximo ao ponto inicial para fechar`}
              </span>
              {secaoEmDesenho.pontos.length > 0 && (
                <button
                  style={btn('#f59e0b', editorLockedByProcessing)}
                  disabled={editorLockedByProcessing}
                  onClick={() => {
                    if (ensureEditorWritable()) {
                      setSecaoEmDesenho(prev => ({ ...prev, pontos: prev.pontos.slice(0, -1) }));
                    }
                  }}
                >
                  Desfazer
                </button>
              )}
              <button style={btn('#ef4444', editorLockedByProcessing)} disabled={editorLockedByProcessing} onClick={() => { if (ensureEditorWritable()) setSecaoEmDesenho(null); }}>Cancelar</button>
            </>
          )}
          {!secaoEmDesenho && secaoSel && (
            <>
              <button style={btn('#f59e0b', editorLockedByProcessing)} disabled={editorLockedByProcessing} onClick={() => {
                if (!ensureEditorWritable()) return;
                const s = secoes.find(x => x.id === secaoSel);
                if (s) abrirEdicaoSecao(s);
              }}>Editar seção</button>
              <button style={btn('#ef4444', editorLockedByProcessing)} disabled={editorLockedByProcessing} onClick={() => {
                if (!ensureEditorWritable()) return;
                setSecoes(prev => prev.filter(s => s.id !== secaoSel));
                setSecaoSel(null);
              }}>Excluir seção</button>
            </>
          )}
        </div>
      )}

      {/* ── Sub-toolbar Rota ── */}
      {modo === 'rota' && (
        <div className="plant-editor-contextbar">
          <span className="plant-editor-context-text">
            Clique para adicionar paradas. Duplo clique na parada para editar. Shift+clique desativa snap.
          </span>
          {secoes.length > 0 && (
            <span className="plant-editor-context-pill">
              {secaoCursor ? secaoCursor.nome : 'Fora de qualquer seção'}
            </span>
          )}
          {rota.length > 0 && (
            <>
              <span className="plant-editor-context-text is-success">{rota.length} parada(s)</span>
              <button
                style={btn('#22c55e', primaryPlantActionDisabled)}
                onClick={handlePrimaryPlantAction}
                disabled={primaryPlantActionDisabled}
                title={routeSaveActionTitle}
              >
                {routeSaveActionLabel}
              </button>
              <button
                style={btn('#ef4444', editorLockedByProcessing)}
                disabled={editorLockedByProcessing}
                onClick={() => { if (ensureEditorWritable()) setRota([]); }}
              >
                Limpar rota
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Sidebar local + canvas ── */}
      <div className={`plant-editor-workarea ${editandoNome || editandoServico ? 'has-context-editor' : ''}`}>
        {!editandoNome && !editandoServico && renderMapSidebar()}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            width: '100%',
            height: 'calc(100vh - 132px)',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid var(--border)',
            borderRadius: 4,
            cursor: editorLockedByProcessing
              ? 'not-allowed'
              : (tipoAdicionar || (modo === 'secoes' && secaoEmDesenho) || modo === 'rota') ? 'crosshair' : 'default',
          }}
        >
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            draggable={true}
            onWheel={handleWheel}
            onClick={handleStageClick}
            onMouseMove={handleStageMouseMove}
            onDragMove={event => { if (event.target === stageRef.current) setPlantView({ x: event.target.x(), y: event.target.y(), scale: event.target.scaleX() }); }}
          >
            {/* Layer 0: Fundo + Grid */}
            <Layer listening={false}>
              <Rect x={0} y={0} width={MAP_W} height={MAP_H} fill="#0d0d1a" />
              {gridLines.map(line => (
                <Line
                  key={line.key}
                  points={line.points}
                  stroke={line.stroke}
                  strokeWidth={line.strokeWidth}
                  listening={false}
                />
              ))}
            </Layer>

            {/* Layer 1: Perímetro — pontos visíveis sempre, interativos só em 'perimetro' */}
            <Layer ref={perimetroLayerRef} listening={modo === 'perimetro' && !editorLockedByProcessing}>
              {fechado && perimetro.length >= 3 && (
                <Line
                  points={perimFlat}
                  closed
                  fill="rgba(255,255,255,0.06)"
                  stroke={perimetroSel && modo === 'perimetro' ? '#ffffff' : '#cbd5e1'}
                  strokeWidth={perimetroSel && modo === 'perimetro' ? 3 : 2}
                  listening={modo === 'perimetro' && !editorLockedByProcessing}
                  onClick={e => {
                    e.cancelBubble = true;
                    setPerimetroSel(true);
                    setElementoSel(null);
                    setSecaoSel(null);
                  }}
                  onDblClick={e => {
                    e.cancelBubble = true;
                    setPerimetroSel(true);
                    setMsg('Perímetro selecionado. Edite arrastando os pontos.');
                    setTimeout(() => setMsg(''), 3000);
                  }}
                />
              )}
              {!fechado && perimetro.length >= 2 && (
                <Line
                  points={perimFlat}
                  stroke="#cbd5e1"
                  strokeWidth={2}
                  listening={false}
                />
              )}
              {perimetro.map((p, i) => (
                <Circle
                  key={i}
                  x={p.x} y={p.y}
                  radius={i === 0 ? 8 : 5}
                  fill={i === 0 ? '#22c55e' : '#e2e8f0'}
                  stroke={i === 0 ? '#16a34a' : '#64748b'}
                  strokeWidth={1.5}
                  draggable={modo === 'perimetro' && !editorLockedByProcessing}
                  listening={modo === 'perimetro' && !editorLockedByProcessing}
                  onClick={e => {
                    e.cancelBubble = true;
                    setPerimetroSel(true);
                    setElementoSel(null);
                    setSecaoSel(null);
                    setRotaSel(null);
                  }}
                  onDragMove={e => {
                    if (editorLockedByProcessing) return;
                    const { x, y } = e.target.position();
                    setPerimetro(prev => prev.map((pt, idx) => idx === i ? { x, y } : pt));
                  }}
                  onDragStart={e => {
                    if (!ensureEditorWritable()) return;
                    rememberDragStartPoint(e);
                    if (stageRef.current) stageRef.current.draggable(false);
                    e.target.getStage().container().style.cursor = 'grabbing';
                  }}
                  onDragEnd={e => {
                    if (stageRef.current) stageRef.current.draggable(true);
                    const origin = consumeDragStartPoint();
                    if (!ensureEditorWritable()) return;
                    const isShift = e.evt.shiftKey;
                    const rawX = e.target.x();
                    const rawY = e.target.y();
                    const point = applyEditorPointPolicy(
                      { x: rawX, y: rawY },
                      {
                        origin,
                        shiftKey: isShift,
                        enableWeld: false,
                      }
                    );
                    e.target.position(point);
                    setPerimetro(prev => prev.map((pt, idx) => idx === i ? point : pt));
                    e.target.getStage().container().style.cursor = 'grab';
                  }}
                  onMouseEnter={e => { e.target.getStage().container().style.cursor = 'grab'; }}
                  onMouseLeave={e => { e.target.getStage().container().style.cursor = 'default'; }}
                />
              ))}
            </Layer>

            {/* Layer 2: Seções — visível em todos os modos, interativo só em 'secoes' */}
            <Layer ref={secoesLayerRef} listening={modo === 'secoes' && !editorLockedByProcessing}>
              {secoes.map(s => {
                const flat = s.pontos.flatMap(p => [p.x, p.y]);
                const cx = s.pontos.reduce((sum, p) => sum + p.x, 0) / s.pontos.length;
                const cy = s.pontos.reduce((sum, p) => sum + p.y, 0) / s.pontos.length;
                return (
                  <React.Fragment key={s.id}>
                    <Line
                      points={flat}
                      closed
                      fill={s.cor + '33'}
                      stroke={secaoSel === s.id ? '#ffffff' : s.cor}
                      strokeWidth={secaoSel === s.id ? 2.5 : 1.5}
                      listening={modo === 'secoes' && !editorLockedByProcessing}
                      onClick={e => {
                        e.cancelBubble = true;
                        setSecaoSel(s.id);
                        setElementoSel(null);
                        setPerimetroSel(false);
                        setRotaSel(null);
                      }}
                      onDblClick={e => {
                        e.cancelBubble = true;
                        setSecaoSel(s.id);
                        setElementoSel(null);
                        setPerimetroSel(false);
                        setRotaSel(null);
                        abrirEdicaoSecao(s);
                      }}
                    />
                    <Text
                      x={cx - 36} y={cy - 8}
                      width={72}
                      text={s.nome}
                      fontSize={11}
                      fill={s.cor}
                      align="center"
                      listening={false}
                    />
                    {modo === 'secoes' && !editorLockedByProcessing && secaoSel === s.id && s.pontos.map((p, i) => (
                      <Circle
                        key={`${s.id}-pt-${i}`}
                        x={p.x} y={p.y}
                        radius={5}
                        fill="#ffffff"
                        stroke={s.cor}
                        strokeWidth={2}
                        draggable
                        listening
                        onClick={e => {
                          e.cancelBubble = true;
                          setSecaoSel(s.id);
                          setRotaSel(null);
                        }}
                        onDragStart={e => {
                          if (!ensureEditorWritable()) return;
                          rememberDragStartPoint(e);
                          if (stageRef.current) stageRef.current.draggable(false);
                          e.target.getStage().container().style.cursor = 'grabbing';
                        }}
                        onDragEnd={e => {
                          if (stageRef.current) stageRef.current.draggable(true);
                          const origin = consumeDragStartPoint();
                          if (!ensureEditorWritable()) return;
                          const isShift = e.evt.shiftKey;
                          const rawX = e.target.x();
                          const rawY = e.target.y();
                          const point = applySectionPointPolicy(
                            { x: rawX, y: rawY },
                            {
                              origin,
                              shiftKey: isShift,
                              secoes,
                              excludeSectionId: s.id,
                            }
                          );
                          e.target.position(point);
                          setSecoes(prev => prev.map(sec =>
                            sec.id !== s.id
                              ? sec
                              : { ...sec, pontos: sec.pontos.map((pt, idx) => idx === i ? point : pt) }
                          ));
                          e.target.getStage().container().style.cursor = 'grab';
                        }}
                        onMouseEnter={e => { e.target.getStage().container().style.cursor = 'grab'; }}
                        onMouseLeave={e => { e.target.getStage().container().style.cursor = 'default'; }}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
              {/* Polígono em desenho */}
              {modo === 'secoes' && !editorLockedByProcessing && secaoEmDesenho && secaoEmDesenho.pontos.length >= 2 && (
                <Line
                  points={secaoEmDesenho.pontos.flatMap(p => [p.x, p.y])}
                  stroke={secaoEmDesenho.cor}
                  strokeWidth={2}
                  dash={[6, 4]}
                  listening={false}
                />
              )}
              {modo === 'secoes' && !editorLockedByProcessing && secaoEmDesenho && secaoEmDesenho.pontos.map((p, i) => (
                <Circle
                  key={i}
                  x={p.x} y={p.y}
                  radius={i === 0 ? 8 : 5}
                  fill={i === 0 ? secaoEmDesenho.cor : '#e2e8f0'}
                  stroke={i === 0 ? secaoEmDesenho.cor : '#64748b'}
                  strokeWidth={1.5}
                  draggable={modo === 'secoes' && !editorLockedByProcessing}
                  listening={modo === 'secoes'}
                />
              ))}
            </Layer>

            {/* Layer 3: Elementos — interativos só em 'elementos' */}
            <Layer ref={elementosLayerRef} listening={modo === 'elementos' && !editorLockedByProcessing}>
              {elementos.map(el => {
                const tipo = TIPOS_ELEMENTO.find(t => t.tipo === el.tipo);
                return (
                  <Group
                    key={el.id}
                    id={el.id}
                    ref={node => {
                      if (node) elementRefs.current[el.id] = node;
                      else delete elementRefs.current[el.id];
                    }}
                    x={el.x} y={el.y}
                    rotation={el.rotation}
                    draggable={modo === 'elementos' && !editorLockedByProcessing}
                    listening={modo === 'elementos' && !editorLockedByProcessing}
                    onClick={e => {
                      e.cancelBubble = true;
                      setElementoSel(el.id);
                      setPerimetroSel(false);
                      setSecaoSel(null);
                      setRotaSel(null);
                      setTipoAdicionar(null);
                      trazerElementoParaFrente(el.id);
                    }}
                    onDblClick={e => {
                      e.cancelBubble = true;
                      setElementoSel(el.id);
                      setPerimetroSel(false);
                      setSecaoSel(null);
                      setRotaSel(null);
                      trazerElementoParaFrente(el.id);
                      if (el.tipo === 'entrada') {
                        let dados = {
                          nomeDisplay: 'Entrada Principal',
                          carrinhos: '',
                          visaoGeral: '',
                          sensacao: '',
                          displayNameSealed: el.displayNameSealed === true,
                        };
                        try {
                          const p = JSON.parse(el.nome);
                          dados = {
                            nomeDisplay: p.nomeDisplay || 'Entrada Principal',
                            carrinhos:   p.carrinhos   || '',
                            visaoGeral:  p.visaoGeral  || '',
                            sensacao:    p.sensacao    || '',
                            displayNameSealed: el.displayNameSealed === true,
                          };
                        } catch {}
                        setEditandoEntrada(el.id);
                        setEntradaTemp(dados);
                      } else if (el.tipo === 'servico') {
                        abrirEdicaoServico(el);
                      } else {
                        setEditandoNome(el.id);
                        setEditandoServico(null);
                      }
                    }}
                    onDragStart={e => {
                      if (!ensureEditorWritable()) return;
                      rememberDragStartPoint(e);
                      if (stageRef.current) stageRef.current.draggable(false);
                    }}
                    onDragEnd={e => {
                      if (stageRef.current) stageRef.current.draggable(true);
                      const origin = consumeDragStartPoint();
                      if (!ensureEditorWritable()) return;
                      const isShift = e.evt.shiftKey;
                      const point = applyEditorPointPolicy(
                        { x: e.target.x(), y: e.target.y() },
                        {
                          origin,
                          shiftKey: isShift,
                          enableWeld: false,
                        }
                      );
                      e.target.position(point);
                      setElementos(prev => prev.map(item =>
                        item.id !== el.id
                          ? item
                          : atualizarPertencimentoElemento(item, secoes, point)
                      ));
                    }}
                    onTransformEnd={onTransformEnd}
                    onMouseEnter={e => { e.target.getStage().container().style.cursor = 'pointer'; }}
                    onMouseLeave={e => { e.target.getStage().container().style.cursor = tipoAdicionar ? 'crosshair' : 'default'; }}
                  >
                    <Rect
                      x={0} y={0}
                      width={el.width} height={el.height}
                      fill={el.tipo === 'entrada' ? '#1a4731' : el.tipo === 'servico' ? '#1e3a5f' : (tipo?.cor || '#6b7280')}
                      opacity={0.88}
                      cornerRadius={el.tipo === 'entrada' || el.tipo === 'servico' ? 8 : 4}
                      stroke={
                        el.tipo === 'entrada' ? (elementoSel === el.id ? '#22c55e' : '#22c55e66') :
                        el.tipo === 'servico' ? (elementoSel === el.id ? '#3b82f6' : '#3b82f666') :
                        (elementoSel === el.id ? '#fff' : 'rgba(255,255,255,0.2)')
                      }
                      strokeWidth={elementoSel === el.id ? 2 : 1}
                    />
                    {(() => {
                      if (el.tipo === 'entrada') {
                        let dadosEnt = {};
                        try { dadosEnt = JSON.parse(el.nome); } catch {}
                        const nomeDisplay = dadosEnt.nomeDisplay || 'Entrada';
                        return (
                          <React.Fragment>
                            <Text
                              x={2} y={el.height / 2 - 16}
                              width={el.width - 4}
                              text="🚪"
                              fontSize={20}
                              fill="#22c55e"
                              align="center"
                              listening={false}
                            />
                            <Text
                              x={2} y={el.height / 2 + 6}
                              width={el.width - 4}
                              text={nomeDisplay}
                              fontSize={9}
                              fill="#22c55e"
                              fontStyle="bold"
                              align="center"
                              wrap="none"
                              ellipsis={true}
                              listening={false}
                            />
                          </React.Fragment>
                        );
                      }
                      if (el.tipo === 'servico') {
                        let dadosServ = {};
                        try { dadosServ = JSON.parse(el.nome); } catch {}
                        const nomeDisplay = (dadosServ.nomeServico || dadosServ.nomeDisplay || 'Serviço').split('\n')[0].trim();
                        return (
                          <React.Fragment>
                            <Text
                              x={2} y={el.height / 2 - 16}
                              width={el.width - 4}
                              text="🛎️"
                              fontSize={20}
                              fill="#3b82f6"
                              align="center"
                              listening={false}
                            />
                            <Text
                              x={2} y={el.height / 2 + 6}
                              width={el.width - 4}
                              text={nomeDisplay}
                              fontSize={9}
                              fill="#93c5fd"
                              fontStyle="bold"
                              align="center"
                              wrap="none"
                              ellipsis={true}
                              listening={false}
                            />
                          </React.Fragment>
                        );
                      }
                      const tipoLabel = tipo?.label.replace(/^\S+\s/, '') || el.tipo;
                      const nomeDescritivo = el.nome && el.nome !== tipoLabel ? el.nome.split('\n')[0].trim() : '';
                      if (nomeDescritivo) {
                        return (
                          <React.Fragment>
                            <Text
                              x={2} y={el.height / 2 - 13}
                              width={el.width - 4}
                              text={tipoLabel}
                              fontSize={9}
                              fill="#94a3b8"
                              align="center"
                              listening={false}
                            />
                            <Text
                              x={2} y={el.height / 2 + 1}
                              width={el.width - 4}
                              text={nomeDescritivo}
                              fontSize={11}
                              fill="#fff"
                              fontStyle="bold"
                              align="center"
                              wrap="none"
                              ellipsis={true}
                              listening={false}
                            />
                          </React.Fragment>
                        );
                      }
                      return (
                        <Text
                          x={2} y={el.height / 2 - 7}
                          width={el.width - 4}
                          text={tipoLabel}
                          fontSize={11}
                          fill="#fff"
                          align="center"
                          listening={false}
                        />
                      );
                    })()}
                  </Group>
                );
              })}
              {!editorLockedByProcessing && (
                <Transformer
                  ref={transformerRef}
                  onTransformEnd={onTransformEnd}
                  boundBoxFunc={(oldBox, newBox) =>
                    newBox.width < 20 || newBox.height < 20 ? oldBox : newBox
                  }
                />
              )}
            </Layer>

            {/* Layer 4: Rota */}
            <Layer ref={rotaLayerRef} listening={modo === 'rota' && !editorLockedByProcessing}>
              {/* Linha conectando as paradas em sequência */}
              {rota.length >= 2 && (
                <Line
                  points={rota.flatMap(p => [p.posicao.x, p.posicao.y])}
                  stroke="#f59e0b"
                  strokeWidth={2}
                  opacity={0.7}
                  listening={false}
                />
              )}
              {/* Preview: último ponto → cursor */}
              {modo === 'rota' && !editorLockedByProcessing && rota.length > 0 && cursorPos && (
                <Line
                  points={[
                    rota[rota.length - 1].posicao.x,
                    rota[rota.length - 1].posicao.y,
                    cursorPos.x,
                    cursorPos.y,
                  ]}
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  dash={[6, 4]}
                  opacity={0.45}
                  listening={false}
                />
              )}
              {/* Marcadores de parada */}
              {rota.map((p, i) => (
                <React.Fragment key={i}>
                  <Circle
                    x={p.posicao.x} y={p.posicao.y}
                    radius={i === rota.length - 1 ? 9 : 6}
                    fill={i === rota.length - 1 ? '#fbbf24' : '#f59e0b'}
                    stroke={rotaSel === i || i === rota.length - 1 ? '#fff' : 'transparent'}
                    strokeWidth={rotaSel === i || i === rota.length - 1 ? 2 : 0}
                    opacity={0.9}
                    draggable={modo === 'rota' && !editorLockedByProcessing}
                    listening={modo === 'rota' && !editorLockedByProcessing}
                    onClick={e => {
                      e.cancelBubble = true;
                      setRotaSel(i);
                      setPerimetroSel(false);
                      setElementoSel(null);
                      setSecaoSel(null);
                    }}
                    onDblClick={e => {
                      e.cancelBubble = true;
                      setRotaSel(i);
                      setEditandoParadaModal(i);
                      setParadaModalTemp({
                        direita: p.direita || '',
                        esquerda: p.esquerda || '',
                        frente: p.frente || '',
                        relation: p.schemaVersion === ROUTE_STOP_FACT_SCHEMA_VERSION
                          ? (p.relation || '')
                          : '',
                        targetKey: p.schemaVersion === ROUTE_STOP_FACT_SCHEMA_VERSION && p.target
                          ? JSON.stringify(p.target)
                          : '',
                      });
                    }}
                    onDragStart={e => {
                      if (!ensureEditorWritable()) return;
                      rememberDragStartPoint(e);
                      if (stageRef.current) stageRef.current.draggable(false);
                    }}
                    onDragEnd={e => {
                      const stage = stageRef.current;
                      if (stage) stage.draggable(true);
                      const origin = consumeDragStartPoint();
                      if (!ensureEditorWritable()) return;
                      const isShift = e.evt.shiftKey;
                      const pos = applyEditorPointPolicy(
                        { x: e.target.x(), y: e.target.y() },
                        {
                          origin,
                          shiftKey: isShift,
                          enableWeld: false,
                        }
                      );
                      setRota(prev => prev.map((parada, idx) =>
                        idx === i ? { ...parada, posicao: pos } : parada
                      ));
                      e.target.position(pos);
                    }}
                    onMouseEnter={e => { e.target.getStage().container().style.cursor = 'pointer'; }}
                    onMouseLeave={e => { e.target.getStage().container().style.cursor = modo === 'rota' ? 'crosshair' : 'default'; }}
                  />
                  <Text
                    x={p.posicao.x + 11} y={p.posicao.y - 8}
                    text={String(i + 1)}
                    fontSize={10}
                    fill="#f59e0b"
                    listening={false}
                  />
                </React.Fragment>
              ))}
            </Layer>
          </Stage>
          {elementos.filter(el => el.tipo === 'servico' && !el.serviceDeclarationId
            && elementos.filter(other => other.id === el.id).length === 1 && clarificationIndex.pending.has(el.id)).map(el => {
            const rotation = Number(el.rotation || 0) * Math.PI / 180;
            const left = plantView.x + (el.x + el.width * Math.cos(rotation)) * plantView.scale + 20;
            const top = plantView.y + (el.y + el.width * Math.sin(rotation)) * plantView.scale - 20;
            return <button key={el.id} type="button" className="plant-clarification-marker"
              style={{ left, top }}
              aria-label={'Abrir esclarecimento pendente do elemento ' + el.id} onClick={() => abrirEdicaoServico(el)}><span aria-hidden="true">!</span></button>;
          })}
        </div>
        {editandoNome && elementoEmEdicao && <StorePlantItemsEditor key={clienteId + ':' + editandoNome}
          element={elementoEmEdicao} section={elementoEmEdicao.secao}
          initialNames={normalizeSemanticHints(draftEdicaoElemento(elementoEmEdicao).itens)}
          aspectChanges={itemAspectDrafts.filter(item => item.elementId === editandoNome)}
          canDeclareAspect={clienteId === CADASTRAL_PILOT} disabled={editorLockedByProcessing}
          onClose={() => setEditandoNome(null)} onApply={salvarEdicaoElemento} />}
      {/* Service authoring and its clarification share the same inline source. */}
      {editandoServico && (
        <div
          ref={serviceEditorRef} className="plant-context-editor"
          style={{
            minWidth: 0,
          }}
        >
          <div style={{
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            width: '100%',
          }}>
            <header><h3>Editar elemento</h3><button type="button" aria-label="Fechar editor de serviço" onClick={() => setEditandoServico(null)}>×</button></header>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Tipo de serviço:</label>
              <select
                autoFocus
                value={servicoTemp.serviceKey}
                onChange={e => { setServiceEdited(true); setServicoTemp(p => ({ ...p, serviceKey: e.target.value })); }}
                disabled={editorLockedByProcessing}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13 }}
              >
                <option value="">Selecione um serviço</option>
                {SERVICOS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
            </div>
            {[
              ['descricao', 'Descrição familiar', 'Ex: Atendimento postal'],
            ].map(([campo, label, placeholder]) => (
              <div key={campo} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>{label}:</label>
                <input
                  value={servicoTemp[campo]}
                  onChange={e => { setServiceEdited(true); setServicoTemp(p => ({ ...p, [campo]: e.target.value })); }}
                  onKeyDown={e => { if (e.key === 'Escape') setEditandoServico(null); }}
                  disabled={editorLockedByProcessing}
                  placeholder={placeholder}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '6px 10px', borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
                  }}
                />
                {!servicoTemp.serviceDeclarationId && <CadastralClarificationReview tenantId={clienteId}
                  sourceKind="LEGACY_PLANT_SERVICE" sourceId={editandoServico} sourceRevision={authoring.revision}
                  sourceReady={clarificationReady && !serviceEdited}
                  onUpdated={() => setClarificationRefreshSeq(previous => previous + 1)} />}
              </div>
            ))}
            <div style={{ color: 'var(--text-secondary)', fontSize: 10, lineHeight: 1.4, marginBottom: 14 }}>
              A posição deste elemento é o local do serviço. O elemento Balcão é outra categoria da planta; delivery é declarado no cadastro e não precisa de elemento.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={() => setEditandoServico(null)}>Cancelar</button>
              <button
                className="plant-context-primary"
                disabled={editorLockedByProcessing}
                onClick={() => {
                  if (!ensureEditorWritable()) return;
                  const serviceKey = String(servicoTemp.serviceKey || '').trim().toUpperCase();
                  const descricao = String(servicoTemp.descricao || '').trim();
                  if (!serviceKey || !SERVICOS.some(item => item.key === serviceKey)) {
                    setMsg('Selecione o tipo de serviço.');
                    return;
                  }
                  if (!descricao) {
                    setMsg('Informe uma descrição familiar para o atendimento.');
                    return;
                  }
                  const novoNome = JSON.stringify({
                    tipo: 'servico',
                    serviceKey,
                    nomeDisplay: descricao,
                    nomeServico: descricao,
                    descricao,
                  });
                  setElementos(prev => prev.map(el => {
                    if (el.id !== editandoServico) return el;
                    const next = { ...el, nome: novoNome };
                    if (servicoTemp.serviceDeclarationId) {
                      next.serviceDeclarationId = servicoTemp.serviceDeclarationId;
                    } else {
                      delete next.serviceDeclarationId;
                    }
                    return next;
                  }));
                  setEditandoServico(null);
                }}
              >
                Aplicar à planta
              </button>
            </div>
            <p className="plant-context-note">As alterações serão gravadas ao salvar a planta.</p>
          </div>
        </div>
      )}


      </div>
      {clarificationIndex.error && <p role="status" className="plant-context-note">{clarificationIndex.error}</p>}

      {/* ── Modal: configurar entrada da loja ── */}
      {editandoEntrada && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={e => e.target === e.currentTarget && setEditandoEntrada(null)}
        >
          <div style={{
            background: '#1a1a2e', border: '1px solid #22c55e44',
            borderRadius: 12, padding: 24, width: 360,
          }}>
            <h3 style={{ color: '#22c55e', margin: '0 0 16px', fontSize: 15 }}>🚪 Configurar Entrada</h3>
            {[
              ['nomeDisplay', 'Nome',        'Ex: Entrada Principal'],
              ['carrinhos',   'Carrinhos',   'Ex: carrinhos organizados à esquerda'],
              ['visaoGeral',  'Visão geral', 'Ex: visão aberta dos corredores'],
              ['sensacao',    'Sensação',    'Ex: primeiro contato com a loja'],
            ].map(([campo, label, placeholder], idx) => (
              <div key={campo} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>{label}:</label>
                <input
                  autoFocus={idx === 0}
                  value={entradaTemp[campo]}
                  onChange={e => setEntradaTemp(p => ({ ...p, [campo]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Escape') setEditandoEntrada(null); }}
                  disabled={editorLockedByProcessing}
                  placeholder={placeholder}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '6px 10px', borderRadius: 6,
                    border: '1px solid #2d2d4e',
                    background: '#16213e', color: '#e2e8f0', fontSize: 13, outline: 'none',
                  }}
                />
              </div>
            ))}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: '#cbd5e1', fontSize: 12, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={entradaTemp.displayNameSealed === true}
                disabled={editorLockedByProcessing}
                onChange={e => setEntradaTemp(previous => ({
                  ...previous,
                  displayNameSealed: e.target.checked,
                }))}
              />
              <span>
                Autorizar o nome exato como dado factual. Os campos de carrinhos,
                visão e sensação continuam editoriais e não entram na locução.
              </span>
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={{ ...btn('#475569'), flex: 1 }} onClick={() => setEditandoEntrada(null)}>Cancelar</button>
              <button
                style={{
                  ...btn(
                    '#166534',
                    editorLockedByProcessing
                      || (entradaTemp.displayNameSealed === true && !entradaTemp.nomeDisplay.trim())
                  ),
                  flex: 1,
                  color: '#fff',
                }}
                disabled={
                  editorLockedByProcessing
                    || (entradaTemp.displayNameSealed === true && !entradaTemp.nomeDisplay.trim())
                }
                onClick={() => {
                  if (!ensureEditorWritable()) return;
                  const nomeDisplay = entradaTemp.nomeDisplay.trim();
                  const novoNome = JSON.stringify({
                    tipo: 'entrada',
                    nomeDisplay,
                    carrinhos: entradaTemp.carrinhos,
                    visaoGeral: entradaTemp.visaoGeral,
                    sensacao: entradaTemp.sensacao,
                  });
                  setElementos(prev => prev.map(el => {
                    if (el.id !== editandoEntrada) return el;
                    const next = {
                      ...el,
                      elementId: el.elementId || el.id,
                      nome: novoNome,
                      entryFactSchemaVersion: ENTRY_FACT_SCHEMA_VERSION,
                      exists: true,
                      displayNameSealed: entradaTemp.displayNameSealed === true,
                    };
                    if (entradaTemp.displayNameSealed === true) {
                      next.displayName = nomeDisplay;
                    } else {
                      delete next.displayName;
                    }
                    return next;
                  }));
                  setEditandoEntrada(null);
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: editar parada da rota ── */}
      {editandoParadaModal !== null && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={e => e.target === e.currentTarget && setEditandoParadaModal(null)}
        >
          <div style={{
            background: '#1a1a2e', border: '1px solid #2d2d4e',
            borderRadius: 12, padding: 24, width: 420,
          }}>
            <h3 style={{ color: '#e2e8f0', margin: '0 0 14px', fontSize: 15 }}>
              ✏️ Parada #{editandoParadaModal + 1}
            </h3>
            {[['direita', 'À direita'], ['esquerda', 'À esquerda'], ['frente', 'À frente']].map(([campo, label], idx) => (
              <div key={campo} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>{label}:</label>
                <input
                  autoFocus={idx === 0}
                  value={paradaModalTemp[campo]}
                  onChange={e => setParadaModalTemp(p => ({ ...p, [campo]: e.target.value }))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      salvarParadaModal();
                    }
                    if (e.key === 'Escape') setEditandoParadaModal(null);
                  }}
                  disabled={editorLockedByProcessing}
                  placeholder="Ex: Iogurtes, Queijos..."
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '6px 10px', borderRadius: 6,
                    border: '1px solid #2d2d4e',
                    background: '#16213e', color: '#e2e8f0', fontSize: 13, outline: 'none',
                  }}
                />
              </div>
            ))}
            <div style={{ padding: 10, border: '1px solid #334155', borderRadius: 8, background: '#111827' }}>
              <div style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                Relação factual para locução
              </div>
              <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>
                Relação tipada
              </label>
              <select
                value={paradaModalTemp.relation}
                disabled={editorLockedByProcessing}
                onChange={e => setParadaModalTemp(previous => ({
                  ...previous,
                  relation: e.target.value,
                }))}
                style={{ width: '100%', padding: '7px 8px', borderRadius: 6, border: '1px solid #2d2d4e', background: '#16213e', color: '#e2e8f0', marginBottom: 8 }}
              >
                <option value="">Não usar na locução</option>
                <option value="RIGHT">À direita</option>
                <option value="LEFT">À esquerda</option>
                <option value="AHEAD">À frente</option>
              </select>
              <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 }}>
                Alvo tipado
              </label>
              <select
                value={paradaModalTemp.targetKey}
                disabled={editorLockedByProcessing}
                onChange={e => setParadaModalTemp(previous => ({
                  ...previous,
                  targetKey: e.target.value,
                }))}
                style={{ width: '100%', padding: '7px 8px', borderRadius: 6, border: '1px solid #2d2d4e', background: '#16213e', color: '#e2e8f0' }}
              >
                <option value="">Nenhum alvo factual</option>
                {spatialTargetOptions.map(option => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
              <div style={{ color: '#94a3b8', fontSize: 10, lineHeight: 1.35, marginTop: 8 }}>
                Os três textos livres acima são editoriais. Só esta relação com
                alvo exato pode entrar na construção semântica.
              </div>
            </div>
            <div style={{ height: 1, background: '#2d2d4e', margin: '12px 0' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#475569', color: '#fff', fontSize: 13, fontWeight: 600, flex: 1 }}
                onClick={() => setEditandoParadaModal(null)}
              >
                Cancelar
              </button>
              <button
                disabled={editorLockedByProcessing || Boolean(paradaModalTemp.relation) !== Boolean(paradaModalTemp.targetKey)}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: editorLockedByProcessing ? 'default' : 'pointer', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, flex: 1, opacity: editorLockedByProcessing || Boolean(paradaModalTemp.relation) !== Boolean(paradaModalTemp.targetKey) ? 0.6 : 1 }}
                onClick={() => {
                  salvarParadaModal();
                }}
              >
                Salvar
              </button>
            </div>
            <button
              disabled={editorLockedByProcessing}
              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: editorLockedByProcessing ? 'default' : 'pointer', background: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 600, width: '100%', marginTop: 8, opacity: editorLockedByProcessing ? 0.6 : 1 }}
              onClick={() => {
                if (!ensureEditorWritable()) return;
                setRota(prev => prev.filter((_, i) => i !== editandoParadaModal));
                setEditandoParadaModal(null);
              }}
            >
              🗑️ Excluir parada
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: editar cadastro declarado da seção ── */}
      {renomeandoSecao && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={e => e.target === e.currentTarget && cancelarEdicaoSecao()}
        >
          <div style={{
            background: '#1a1a2e', border: '1px solid #2d2d4e',
            borderRadius: 12, padding: 24, width: 340,
          }}>
            <h3 style={{ color: '#e2e8f0', margin: '0 0 14px', fontSize: 15 }}>
              {secaoNovaPendenteId === renomeandoSecao ? 'Nomear seção' : 'Editar seção'}
            </h3>
            <label style={{ display: 'block', color: '#cbd5e1', fontSize: 12, marginBottom: 6 }}>
              Nome declarado da seção *
            </label>
            <input
              value={nomeSecaoTemp}
              onChange={e => setNomeSecaoTemp(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  salvarEdicaoSecao();
                }
                if (e.key === 'Escape') cancelarEdicaoSecao();
              }}
              autoFocus
              disabled={editorLockedByProcessing}
              maxLength={SECTION_NAME_MAX_LENGTH}
              placeholder="Ex: Bebidas, Limpeza, Hortifruti..."
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 12px', borderRadius: 8,
                border: '1px solid #2d2d4e',
                background: '#16213e', color: '#e2e8f0', fontSize: 14,
                marginBottom: nomeSecaoErro ? 6 : 14, outline: 'none',
              }}
            />
            {nomeSecaoErro && (
              <small className="is-error" style={{ display: 'block', marginBottom: 14 }}>
                {nomeSecaoErro}
              </small>
            )}
            <div className="plant-editor-section-registration">
              <label className="plant-editor-section-sensitive">
                <input
                  type="checkbox"
                  checked={sensivelSecaoTemp}
                  disabled={editorLockedByProcessing}
                  onChange={event => setSensivelSecaoTemp(event.target.checked)}
                />
                Seção sensível
              </label>
              <small>
                Se marcada, a seção não recebe fala automática. Texto integral do
                operador só atravessa esse gate quando ligado à seção exata e permitido
                pela política; farmácia e medicamentos continuam bloqueados.
              </small>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 6 }}>Dias ativos *</label>
              <div style={{ display: 'flex', gap: 4 }}>
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((label, idx) => {
                  const ativo = diasTemp.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={editorLockedByProcessing}
                      onClick={() => setDiasTemp(prev =>
                        ativo ? prev.filter(d => d !== idx) : [...prev, idx].sort((a, b) => a - b)
                      )}
                      style={{
                        flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', cursor: editorLockedByProcessing ? 'default' : 'pointer',
                        background: ativo ? '#2563ab' : '#1e293b',
                        color: ativo ? '#fff' : '#94a3b8',
                        fontSize: 11, fontWeight: 600,
                        opacity: editorLockedByProcessing ? 0.6 : 1,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {diasTemp.length === 0 && (
                <small className="is-error" style={{ display: 'block', marginTop: 6 }}>
                  Selecione ao menos um dia ativo.
                </small>
              )}
            </div>
            <div style={{ marginTop: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>
                <span>Prioridade *</span>
                <span style={{ color: '#fff', fontWeight: 'bold', fontSize: 13 }}>{pesoSecaoTemp}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={pesoSecaoTemp}
                onChange={e => setPesoSecaoTemp(Number(e.target.value))}
                disabled={editorLockedByProcessing}
                style={{ width: '100%', accentColor: '#2563ab' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                <span>Menor frequência</span>
                <span>Maior frequência</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...btn('#475569'), flex: 1 }} onClick={cancelarEdicaoSecao}>Cancelar</button>
              <button
                style={{ ...btn('#3b82f6', editorLockedByProcessing || cadastroSecaoInvalido), flex: 1 }}
                disabled={editorLockedByProcessing || cadastroSecaoInvalido}
                onClick={salvarEdicaoSecao}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogPortal />
    </div>
  );
}
